import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  createContext,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
// Build sync: 2026-05-07 23:11

import Draggable, { DraggableEvent, DraggableData } from "react-draggable";
import TaskTooltip from "./TaskTooltip";
import { IoMdArrowDropup } from "react-icons/io";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMinusSquare,
  faPlusSquare,
  faPlus,
  faFolder,
  faFolderOpen,
  faFileAlt,
} from "@fortawesome/free-solid-svg-icons";
import { throttle } from "lodash";

import {
  Box,
  Text,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tooltip,
  useDisclosure,
  Textarea,
  Accordion,
  AccordionIcon,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Badge,
  Heading,
  Tag,
  TagCloseButton,
  TagLabel,
  HStack,
  VStack,
  IconButton,
  Spacer,
} from "@chakra-ui/react";

import {
  Menu as ChakraMenu,
  MenuButton as ChakraMenuButton,
  MenuList as ChakraMenuList,
  MenuItem as ChakraMenuItem,
  Portal,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { db, auth } from "../firebase/firebase";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  onSnapshot,
} from "@firebase/firestore";

import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MarkerType,
  useEdgesState,
  useNodesState,
} from "reactflow";
import { AddIcon, DeleteIcon, DownloadIcon, EditIcon } from "@chakra-ui/icons";
import { Connection, Edge } from "react-flow-renderer";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ReactToPrint from "react-to-print";
import { Tasks } from "../pages/Gantt/GanttMain";
import { format, parse, isValid } from "date-fns";
import { AiFillPrinter } from "react-icons/ai";
import { FiMaximize, FiMinimize, FiUpload } from "react-icons/fi";
import { FaFileImport } from "react-icons/fa";
import AssignedUsersPopover from "./AssignedUsersPopover";
import axios from "axios";
import { FiArrowLeft, FiChevronRight } from "react-icons/fi";
import { FaBuilding } from "react-icons/fa";
import { IconWrapper } from "../components/IconWrapper";
import { logToSystem } from "../utils/systemLogging";
import { getTaskPermissions, getProjectScopeAccess, filterTaskDataByAssignment } from "../utils/permissions";
import ResourceView from "../pages/Gantt/ResourceView";
import { BsPeopleFill } from "react-icons/bs";
import { showToast } from "../utils/toastUtils";

interface Task {
  id: string;
  name: string;
  plannedStart: string;
  plannedEnd: string;
  actualStart: string;
  actualEnd: string;
  duration: number;
  actualDuration?: number;
  dependency: string;
  risk: string;
  wbs: string;
  inlineProgress?: number;
  progress: number;
  predecessor?: string[];
  role?: string;
  status: string;
  type: string;
  stage?: string;
  parentId?: string;
  subtasks?: Task[];
  assignedUsers?: string[];
  _displayEmails?: string;
  dependencies?: { taskId: string; type: "FS" | "SS" | "FF" | "SF" }[];
  createdBy?: string;
}

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  assignedProjects?: string[];
  assignedTasks?: {
    id: string;
    name: string;
    taskId: string;
    channelId: string;
    projectId: string;
  }[];
  organizationId: string;
  permissions?: string;
};

export type FilterProject = {
  id: string;
  name: string;
  description?: string;
  channels: string[];
  assignedUsers?: string[];
  status?: string;
};

interface GanttProps {
  selectedTask: Tasks;
  organizationId: string;
  projectId: string;
  channelId: string;
  bg: string;
  color: string;
  users: any[];
  filteredProjects: FilterProject[];
  onExit?: () => void;
  currentUser: any;
}

const TaskContext = createContext<{
  expandedTaskIds: Set<string>;
  toggleExpandedTask: (taskId: string) => void;
} | null>(null);

const backendUrl = "https://api-xk2q5lbbua-uc.a.run.app";

const TaskListPanel = forwardRef(
  (
    {
      tasks,
      users,
      currentUser,
      filteredProjects,
      setTask,
      width,
      projectId,
      taskId,
      channelId,
      TaskName,
      color,
      bg,
      selectedTaskId,
      organizationId,
      onSelectTask,
      zoomLevel,
      expandedTaskId,
      setExpandedTaskId,
      isFullscreen,
      fullscreenContainerRef,
    }: {
      tasks: Task[];
      users: User[];
      filteredProjects: FilterProject[];
      setTask: React.Dispatch<React.SetStateAction<Task[]>>;
      width: string;
      projectId: string;
      taskId: string | null;
      channelId: string;
      TaskName: string;
      color: string;
      bg: string;
      selectedTaskId: string;
      organizationId: string;
      onSelectTask: (id: string) => void;
      zoomLevel: string;
      expandedTaskId: string | null;
      setExpandedTaskId: React.Dispatch<React.SetStateAction<string | null>>;
      isFullscreen: boolean;
      fullscreenContainerRef: React.RefObject<HTMLDivElement | null>;
      currentUser: any;
    },
    ref
  ) => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === "dark";
    const componentRef = useRef<React.ComponentType>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isProgressFocused, setIsProgressFocused] = useState(false);
    const { expandedTaskIds, toggleExpandedTask } = useTaskContext();

    // Color mode values for consistent theming
    const goldTextColor = useColorModeValue("yellow.600", "yellow.400");
    const goldBgColor = useColorModeValue("yellow.50", "transparent");
    const inputTextColor = useColorModeValue("black", "white");
    const modalHeaderColor = useColorModeValue("yellow.600", "yellow.400");
    const buttonTextColor = useColorModeValue("black", "white");
    const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteReason, setDeleteReason] = useState("");
    const [isAuditModalOpen, setIsAuditModalOpen] = useState(false); // Modal state
    const [selectedAudit, setSelectedAudit] = useState<any[]>([]); // Audit data for the selected task
    const [selectedTaskName, setSelectedTaskName] = useState<string>("");
    const [isDeletedAuditModalOpen, setIsDeletedAuditModalOpen] =
      useState(false);
    const [deletedAudits, setDeletedAudits] = useState<any[]>([]);
    const [isproDragging, setIsProDragging] = useState(false);
    
    // New states for Import Review
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [pendingChanges, setPendingChanges] = useState<{
      updates: { wbs: string, name: string, changes: { field: string, old: any, new: any }[] }[],
      newTasks: Task[],
      allImportedIds: Set<string>,
      fullImportedTree: Task[]
    }>({ updates: [], newTasks: [], allImportedIds: new Set(), fullImportedTree: [] });

    const taskPerms = React.useMemo(
      () => getTaskPermissions(currentUser),
      [currentUser]
    );
    const { isAdmin, canCreate, canEdit, canDelete } = taskPerms;

    // A per-project admin/editor grant adds task add/edit rights on top of the
    // org-wide permission. It never adds delete rights, so canDelete is left
    // untouched.
    const projectScope = React.useMemo(
      () => getProjectScopeAccess(currentUser, projectId),
      [currentUser, projectId]
    );
    const canCreateTask = canCreate || projectScope.canManageTasks;
    const canEditTask = React.useCallback(
      (task: any) => canEdit(task) || projectScope.canManageTasks,
      [canEdit, projectScope]
    );


    const showPermissionToast = () => {
      showToast({
        title: "Permission Restricted",
        description: "You do not have permission to perform this action.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    };
    const [newTask, setNewTask] = useState<Task>({
      id: "",
      wbs: "",
      name: "",
      parentId: "0",
      plannedStart: "",
      plannedEnd: "",
      actualStart: "",
      actualEnd: "",
      duration: 0,
      dependency: "",
      risk: "Low",
      progress: 0,
      type: "task",
      role: "user",
      stage: "DR-1",
      assignedUsers: [],
      status: "Not-started",
      actualDuration: 0,
      subtasks: [],
      createdBy: currentUser?.id || "",
    });

    const canEditCurrentTask = React.useMemo(() => {
      if (!editingTaskId) {
        return canCreateTask;
      }
      return canEditTask(newTask || undefined);
    }, [canCreateTask, canEditTask, editingTaskId, newTask]);

    const [isSavingOrUpdating, setIsSavingOrUpdating] = useState(false);

    const ProjectName =
      filteredProjects.find((p) => p.id === projectId)?.name ||
      "Untitled Project";

    // Format values for the audit table (e.g., map assigned user IDs to names)
    const formatAuditValue = (field: string, value: any): string => {
      if (value === null || value === undefined || value === "") return "N/A";
      if (field === "assignedUsers") {
        // Handle both array and comma-separated string formats
        const valuesArray = Array.isArray(value)
          ? value
          : typeof value === "string"
          ? value.split(",").map((s) => s.trim())
          : [value];

        const names = valuesArray
          .map((id: any) => {
            if (!id) return null;
            const matchedUser = users?.find((u: any) => u.id === id);
            return matchedUser
              ? `${matchedUser.firstName} ${matchedUser.lastName}`
              : null; // Don't show if not in organization members
          })
          .filter(Boolean);

        return names.length > 0 ? names.join(", ") : "N/A";
      }
      if (Array.isArray(value)) return value.join(", ");
      if (typeof value === "object") {
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
    };

    const formatDateForDisplay = (dateStr: string) => {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        return format(date, "dd/MM/yyyy");
      } catch (e) {
        return dateStr;
      }
    };

    const parseImportDate = (dateVal: any) => {
      if (!dateVal) return "";
      const s = String(dateVal).trim();
      if (!s) return "";

      // Try DD/MM/YYYY
      const dateParse = parse(s, "dd/MM/yyyy", new Date());
      if (isValid(dateParse)) {
        return format(dateParse, "yyyy-MM-dd");
      }

      // Fallback to standard parsing (handles YYYY-MM-DD or date objects)
      const d = new Date(s);
      if (!isNaN(d.getTime())) {
        return format(d, "yyyy-MM-dd");
      }
      return s;
    };

    const flattenTasks = (
      tasks: Task[],
      parentWbs: string = "",
      level: number = 0
    ): any[] => {
      let flatList: any[] = [];

      tasks.forEach((task, index) => {
        const currentWbs = parentWbs
          ? `${parentWbs}.${index + 1}`
          : `${index + 1}`;
        flatList.push({
          id: task.id,
          WBS: currentWbs,
          Name: task.name,
          ParentID: task.parentId,
          PlannedStart: task.plannedStart,
          PlannedEnd: task.plannedEnd,
          ActualStart: task.actualStart,
          ActualEnd: task.actualEnd,
          Duration: task.duration,
          Dependency: task.dependency,
          Risk: task.risk,
          Progress: task.progress,
          Type: task.type,
          Role: task.role,
          Stage: task.stage,
          Status: task.status,
          ActualDuration: task.actualDuration,
          Level: level,
        });

        if (task.subtasks && task.subtasks.length > 0) {
          flatList = flatList.concat(
            flattenTasks(task.subtasks, currentWbs, level + 1)
          );
        }
      });

      return flatList;
    };

    const handleExportClick = async () => {
      try {
        if (!tasks || tasks.length === 0) {
          showToast({
            title: "No Data",
            description: "There are no tasks to export.",
            status: "info",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
          return;
        }

        const flatTasks = flattenTasks(tasks);
        
        // Remove columns we don't want in Excel and format dates
        const exportData = flatTasks.map(({ id, Role, ...rest }) => ({
          ...rest,
          PlannedStart: formatDateForDisplay(rest.PlannedStart),
          PlannedEnd: formatDateForDisplay(rest.PlannedEnd),
          ActualStart: formatDateForDisplay(rest.ActualStart),
          ActualEnd: formatDateForDisplay(rest.ActualEnd),
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);

        // Set column widths (Adjusted for removed columns)
        const wscols = [
          { wpx: 80 },  // WBS
          { wpx: 200 }, // Name
          { wpx: 100 }, // ParentID
          { wpx: 120 }, // PlannedStart
          { wpx: 120 }, // PlannedEnd
          { wpx: 120 }, // ActualStart
          { wpx: 120 }, // ActualEnd
          { wpx: 80 },  // Duration
          { wpx: 100 }, // Dependency
          { wpx: 80 },  // Risk
          { wpx: 80 },  // Progress
          { wpx: 100 }, // Type
          { wpx: 100 }, // Stage
          { wpx: 120 }, // Status
          { wpx: 120 }, // ActualDuration
          { hidden: true }, // Level
        ];

        worksheet["!cols"] = wscols;

        // Add autofilter and freeze panes
        worksheet["!autofilter"] = { ref: "A1:O1" };
        worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Tasks");

        const workbookBinary = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        const blob = new Blob([workbookBinary], {
          type: "application/octet-stream",
        });
        saveAs(blob, `${TaskName}.xlsx`);

        showToast({
          title: "Export Successful",
          description: "Tasks have been exported to Excel successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });

        // Log the export action
        try {
          const currentUserAuth = auth.currentUser;
          if (currentUserAuth && organizationId) {
            const userRef = doc(db, "users", currentUserAuth.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const userData = userSnap.data();
              await logToSystem(organizationId, "actions", {
                type: "ACTIVITY",
                user: `${userData.firstName} ${userData.lastName}`,
                email: userData.email,
                action: "Task Management",
                details: `Exported ${tasks.length} tasks/subtasks from ${TaskName} to Excel for project ${ProjectName}`,
                status: "success",
              });
            }
          }
        } catch (logError) {
          console.error("Error logging export:", logError);
        }
      } catch (error) {
        console.error("Export Error:", error);
        showToast({
          title: "Export Failed",
          description: "An error occurred while exporting tasks.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    };

    const handleDownloadTemplate = () => {
      const templateData = [
        {
          WBS: "1",
          Name: "Sample Task",
          ParentID: "0",
          PlannedStart: "01/01/2024",
          PlannedEnd: "05/01/2024",
          ActualStart: "01/01/2024",
          ActualEnd: "05/01/2024",
          Duration: 5,
          Dependency: "",
          Risk: "Low",
          Progress: 0,
          Type: "task",
          Stage: "DR-1",
          Status: "Not-started",
          ActualDuration: 5,
          "Assigned Users": "user1@example.com, user2@example.com",
          Level: 0
        },
        {
          WBS: "1.1",
          Name: "Sample Subtask",
          ParentID: "1",
          PlannedStart: "01/01/2024",
          PlannedEnd: "02/01/2024",
          ActualStart: "01/01/2024",
          ActualEnd: "02/01/2024",
          Duration: 2,
          Dependency: "",
          Risk: "Low",
          Progress: 0,
          Type: "task",
          Stage: "DR-1",
          Status: "Not-started",
          ActualDuration: 2,
          "Assigned Users": "user3@example.com",
          Level: 1
        }
      ];

      const worksheet = XLSX.utils.json_to_sheet(templateData);
      const wscols = [
        { wpx: 80 },  // WBS
        { wpx: 200 }, // Name
        { wpx: 100 }, // ParentID
        { wpx: 120 }, // PlannedStart
        { wpx: 120 }, // PlannedEnd
        { wpx: 120 }, // ActualStart
        { wpx: 120 }, // ActualEnd
        { wpx: 80 },  // Duration
        { wpx: 100 }, // Dependency
        { wpx: 80 },  // Risk
        { wpx: 80 },  // Progress
        { wpx: 100 }, // Type
        { wpx: 100 }, // Stage
        { wpx: 120 }, // Status
        { wpx: 120 }, // ActualDuration
        { wpx: 250 }, // Assigned Users
        { hidden: true }, // Level
      ];
      worksheet["!cols"] = wscols;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
      const workbookBinary = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([workbookBinary], { type: "application/octet-stream" });
      saveAs(blob, "Task_Import_Template.xlsx");
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImportClick = () => {
      if (!isAdmin) {
        showPermissionToast();
        return;
      }
      fileInputRef.current?.click();
    };

    const handleSubtaskAddClick = (parentId?: string) => {
      if (!canCreateTask) {
        showPermissionToast();
        return;
      }
      handleAddTaskClick(parentId);
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const bstr = evt.target?.result;
          const wb = XLSX.read(bstr, { type: "binary" });
          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          const data = XLSX.utils.sheet_to_json(ws) as any[];

          if (data.length === 0) {
            showToast({
              title: "Import Error",
              description: "The Excel file is empty.",
              status: "error",
              duration: 3000,
              isClosable: true,
            });
            return;
          }

          // 0. Identify column name (flexible)
          const keys = Object.keys(data[0] || {});
          const assignedUsersKey = keys.find(k => /assigned\s*users\s*\(emails\)/i.test(k)) || 
                                   keys.find(k => /assigned\s*users/i.test(k));

          // 1. Validate emails and build mapping
          const emailToIdMap = new Map();
          users.forEach(u => {
            if (u.email) {
              emailToIdMap.set(u.email.toLowerCase().trim(), u.id);
            }
          });

          const allMissingEmails: string[] = [];
          const allAssignedIds = new Set<string>();

          data.forEach((row, index) => {
            const emailStr = assignedUsersKey ? row[assignedUsersKey] : "";
            if (emailStr) {
              const emails = String(emailStr).split(",").map(e => e.trim().toLowerCase()).filter(Boolean);
              emails.forEach(email => {
                const userId = emailToIdMap.get(email);
                if (!userId) {
                  allMissingEmails.push(`${email} (Row ${index + 2})`);
                } else {
                  allAssignedIds.add(userId);
                }
              });
            }
          });

          if (allMissingEmails.length > 0) {
            showToast({
              title: "Import Cancelled",
              description: `Missing users in organization: ${allMissingEmails.slice(0, 3).join(", ")}${allMissingEmails.length > 3 ? "..." : ""}. Please ensure all users exist before importing.`,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            return;
          }

          // 3. Prepare Preview Data & Merge Logic
          const existingFlat = flattenTasks(tasks);
          const existingMap = new Map(existingFlat.map(t => [t.WBS, t]));
          
          // Initialize map with ALL existing tasks to ensure we don't delete them
          const wbsMap: Record<string, Task> = {};
          
          // We need a helper to get original Task objects from existing tree
          const allExistingTasksMap = new Map<string, Task>();
          const collectTasks = (list: Task[]) => {
            list.forEach(t => {
              // Clone to avoid mutation of prop
              const clone = { ...t, subtasks: [] };
              allExistingTasksMap.set(t.wbs, clone);
              if (t.subtasks) collectTasks(t.subtasks);
            });
          };
          collectTasks(tasks);
          
          // Fill wbsMap with clones of ALL existing tasks
          allExistingTasksMap.forEach((task, wbs) => {
            wbsMap[wbs] = task;
          });

          const updates: any[] = [];
          const newTasks: Task[] = [];
          const allAssignedIdsInImport = new Set<string>();

          // Sort by WBS to ensure parents are processed before children
          const sortedData = [...data].sort((a, b) => {
            const wbsA = String(a.WBS || "").split(".").map(Number);
            const wbsB = String(b.WBS || "").split(".").map(Number);
            for (let i = 0; i < Math.max(wbsA.length, wbsB.length); i++) {
              if ((wbsA[i] || 0) !== (wbsB[i] || 0)) return (wbsA[i] || 0) - (wbsB[i] || 0);
            }
            return 0;
          });

          sortedData.forEach(row => {
            const emailStr = assignedUsersKey ? row[assignedUsersKey] : "";
            const assignedUserIds = emailStr
              ? String(emailStr).split(",").map(e => emailToIdMap.get(e.trim().toLowerCase())).filter(Boolean)
              : [];

            const wbs = String(row.WBS || "");
            const existingInExcel = existingMap.get(wbs);
            
            const importedTask: Task = {
              id: existingInExcel ? existingInExcel.id : (Date.now().toString() + Math.random().toString(36).substr(2, 9)),
              name: String(row.Name || "").trim(),
              wbs: wbs,
              parentId: String(row.ParentID || "0"),
              plannedStart: parseImportDate(row.PlannedStart || ""),
              plannedEnd: parseImportDate(row.PlannedEnd || ""),
              actualStart: parseImportDate(row.ActualStart || ""),
              actualEnd: parseImportDate(row.ActualEnd || ""),
              duration: Number(row.Duration || 0),
              actualDuration: Number(row.ActualDuration || 0),
              dependency: String(row.Dependency || ""),
              risk: String(row.Risk || "low"),
              progress: Number(row.Progress || 0),
              type: String(row.Type || "task"),
              role: String(row.Role || "user"),
              stage: String(row.Stage || "DR-1"),
              status: String(row.Status || "Not-started"),
              assignedUsers: assignedUserIds,
              subtasks: [],
              // For display in review modal
              _displayEmails: emailStr
            };

            // Safety: Remove any undefined properties
            Object.keys(importedTask).forEach(key => {
              if ((importedTask as any)[key] === undefined) {
                delete (importedTask as any)[key];
              }
            });

            if (existingInExcel) {
              const taskChanges: { field: string, old: any, new: any }[] = [];
              const fieldsToCompare = [
                { key: 'name', label: 'Name', existingKey: 'Name' },
                { key: 'plannedStart', label: 'Planned Start', existingKey: 'PlannedStart' },
                { key: 'plannedEnd', label: 'Planned End', existingKey: 'PlannedEnd' },
                { key: 'actualStart', label: 'Actual Start', existingKey: 'ActualStart' },
                { key: 'actualEnd', label: 'Actual End', existingKey: 'ActualEnd' },
                { key: 'duration', label: 'Duration', existingKey: 'Duration' },
                { key: 'status', label: 'Status', existingKey: 'Status' },
                { key: 'risk', label: 'Risk', existingKey: 'Risk' }
              ];

              fieldsToCompare.forEach(f => {
                const oldVal = String((existingInExcel as any)[f.existingKey] || "").trim();
                const newVal = String((importedTask as any)[f.key] || "").trim();
                if (oldVal !== newVal) {
                  const isDate = f.key.toLowerCase().includes('start') || f.key.toLowerCase().includes('end');
                  taskChanges.push({ 
                    field: f.label, 
                    old: isDate ? formatDateForDisplay(oldVal) : oldVal, 
                    new: isDate ? formatDateForDisplay(newVal) : newVal 
                  });
                }
              });

              if (taskChanges.length > 0) {
                updates.push({ wbs, name: importedTask.name, changes: taskChanges });
              }
              
              // Preserve existing metadata
              const originalTask = allExistingTasksMap.get(wbs);
              if (originalTask) {
                importedTask.dependencies = originalTask.dependencies || [];
                importedTask.predecessor = originalTask.predecessor || [];
              }
            } else {
              newTasks.push(importedTask);
            }

            assignedUserIds.forEach(id => allAssignedIdsInImport.add(id));
            
            // Overwrite or Add to the map
            wbsMap[importedTask.wbs] = importedTask;
          });

          // 4. Reconstruct the full tree from merged wbsMap
          const rootTasks: Task[] = [];
          
          // Important: Sort ALL tasks in the map by WBS for consistent tree building
          const allMergedTasks = Object.values(wbsMap).sort((a, b) => {
            const wbsA = a.wbs.split(".").map(Number);
            const wbsB = b.wbs.split(".").map(Number);
            for (let i = 0; i < Math.max(wbsA.length, wbsB.length); i++) {
              if ((wbsA[i] || 0) !== (wbsB[i] || 0)) return (wbsA[i] || 0) - (wbsB[i] || 0);
            }
            return 0;
          });

          allMergedTasks.forEach(t => {
            const wbsParts = t.wbs.split(".");
            if (wbsParts.length === 1) {
              rootTasks.push(t);
            } else {
              const parentWbs = wbsParts.slice(0, -1).join(".");
              if (wbsMap[parentWbs]) {
                if (!wbsMap[parentWbs].subtasks) wbsMap[parentWbs].subtasks = [];
                wbsMap[parentWbs].subtasks!.push(t);
              } else {
                rootTasks.push(t); // Orphan becomes root
              }
            }
          });

          setPendingChanges({
            updates,
            newTasks,
            allImportedIds: allAssignedIdsInImport,
            fullImportedTree: rootTasks
          });
          setIsReviewModalOpen(true);

        } catch (error) {
          console.error("Import Error:", error);
          showToast({
            title: "Import Failed",
            description: "An error occurred while parsing the Excel file. Please check the format.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        } finally {
          setIsSavingOrUpdating(false);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      };
      reader.readAsBinaryString(file);
    };

    const handleConfirmImport = async () => {
      if (!taskId) {
        showToast({ title: "Import Error", description: "Task ID is missing.", status: "error" });
        return;
      }
      setIsSavingOrUpdating(true);
      try {
        // Clean up temporary UI fields from the tree
        const cleanTree = (list: Task[]): Task[] => {
          return list.map(t => {
            const { _displayEmails, ...rest } = t as any;
            if (rest.subtasks) rest.subtasks = cleanTree(rest.subtasks);
            return rest as Task;
          });
        };

        const finalTree = cleanTree(pendingChanges.fullImportedTree);

        const taskDocRef = doc(
          db,
          `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
        );
        await setDoc(taskDocRef, { data: finalTree }, { merge: true });

        if (pendingChanges.allImportedIds.size > 0) {
          const projectRef = doc(db, `organization/${organizationId}/projects/${projectId}`);
          await updateDoc(projectRef, {
            assignedUsers: arrayUnion(...Array.from(pendingChanges.allImportedIds))
          });
        }

        showToast({
          title: "Import Successful",
          description: "Tasks have been updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Log
        try {
          const currentUserAuth = auth.currentUser;
          if (currentUserAuth && organizationId) {
            const userRef = doc(db, "users", currentUserAuth.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              await logToSystem(organizationId, "actions", {
                type: "ACTIVITY",
                user: `${userData.firstName} ${userData.lastName}`,
                email: userData.email,
                action: "Task Management",
                details: `Bulk imported/updated tasks via Excel for project ${ProjectName}`,
                status: "success",
              });
            }
          }
        } catch (e) {
          console.error("Logging error:", e);
        }

        setIsReviewModalOpen(false);
      } catch (error: any) {
        console.error("Import Error:", error);
        showToast({ 
          title: "Import Failed", 
          description: error.message || "An error occurred during save.", 
          status: "error",
          duration: 5000,
          isClosable: true
        });
      } finally {
        setIsSavingOrUpdating(false);
      }
    };





    const handleRowDoubleClick = (task: Task, parentTaskId?: string) => {
      if (!canEditTask(task)) {
        showPermissionToast();
        return;
      }
      if (parentTaskId) {
        const parentTask = tasks.find((t) => t.id === parentTaskId);
        const subtaskToEdit = parentTask?.subtasks?.find(
          (sub) => sub.id === task.id
        );
        if (subtaskToEdit) {
          setNewTask({ ...subtaskToEdit });
          setEditingTaskId(task.id);
        }
      } else {
        // Editing a parent task
        setNewTask({ ...task });
        setEditingTaskId(task.id);
      }
      setIsModalOpen(true); // Open modal
    };

    const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({
      x: 0,
      y: 0,
    });
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (newTask.plannedStart) {
        const startDate = new Date(newTask.plannedStart);

        const durationDays = Math.max(newTask.duration || 0, 1);
        // duration is inclusive of the start day: duration=1 => end = start
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + (durationDays - 1));

        const plannedEndDate = endDate.toISOString().split("T")[0];

        setNewTask((prev) => ({
          ...prev,
          duration: durationDays,
          plannedEnd: plannedEndDate,
        }));
      } else if (newTask.duration === 0 || newTask.duration === null) {
        setNewTask((prev) => ({
          ...prev,
          plannedEnd: "", // Reset plannedEnd if no start date is selected
        }));
      }
    }, [newTask.plannedStart, newTask.duration]);

    useEffect(() => {
      if (newTask.actualStart) {
        const startDate = new Date(newTask.actualStart);

        const durationDays = Math.max(newTask.actualDuration || 0, 1);
        // inclusive: duration=1 => end = start
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + (durationDays - 1));

        const actualEndDate = endDate.toISOString().split("T")[0];

        setNewTask((prev) => ({
          ...prev,
          actualDuration: durationDays,
          actualEnd: actualEndDate,
        }));
      } else if (
        newTask.actualDuration === 0 ||
        newTask.actualDuration === null
      ) {
        setNewTask((prev) => ({
          ...prev,
          actualEnd: "", // Reset actualEnd if no start date is selected
        }));
      }
    }, [newTask.actualStart, newTask.actualDuration]);

    const findTaskById = (tasks: Task[], taskId: string): Task | undefined => {
      for (const task of tasks) {
        if (task.id === taskId) return task; // Task found at the current level
        if (task.subtasks && task.subtasks.length > 0) {
          const foundTask = findTaskById(task.subtasks, taskId); // Search in subtasks
          if (foundTask) return foundTask;
        }
      }
      return undefined; // Task not found
    };

    const handleAddTaskClick = (parentId?: string) => {
      const generateRandomId = () =>
        Date.now().toString() + Math.floor(Math.random() * 1000);

      if (parentId) {
        const parentTask = findTaskById(tasks, parentId);
        if (!parentTask) {
          console.error("Parent task not found for ID:", parentId);
          return;
        }
        const subtaskNumber = (parentTask.subtasks?.length || 0) + 1;
        const newWbs = `${parentTask.wbs}.${subtaskNumber}`;
        setNewTask({
          id: generateRandomId(),
          wbs: newWbs,
          name: "",
          plannedStart: "",
          plannedEnd: "",
          actualStart: "",
          actualEnd: "",
          duration: 0,
          dependency: "",
          risk: "low",
          progress: 0,
          type: "task",
          parentId: parentId,
          role: "user",
          stage: "DR-1",
          status: "Not-started",
          actualDuration: 0,
          subtasks: [],
          assignedUsers: [],
          createdBy: currentUser?.id || currentUser?.uid || "",
        });
      } else {
        // Creating a parent task
        setNewTask({
          id: generateRandomId(),
          wbs: `${tasks.length + 1}`,
          name: "",
          plannedStart: "",
          plannedEnd: "",
          actualStart: "",
          actualEnd: "",
          duration: 0,
          dependency: "",
          risk: "low",
          parentId: "0",
          progress: 0,
          type: "task",
          role: "user",
          stage: "DR-1 ",
          status: "Not-started",
          actualDuration: 0,
          subtasks: [],
          assignedUsers: [],
          createdBy: currentUser?.id || currentUser?.uid || "",
        });
      }
      setEditingTaskId(null); // Reset editing task ID for new tasks
      setIsModalOpen(true); // Open modal
    };
    const handleCloseModal = () => {
      setIsModalOpen(false);
      setEditingTaskId(null);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      // Ensure selecting a start date sets minimum duration to 1 day
      if (name === "plannedStart") {
        setNewTask((prev) => ({
          ...prev,
          plannedStart: value,
          duration: Math.max(prev.duration || 0, 1),
        }));
        return;
      }
      if (name === "actualStart") {
        setNewTask((prev) => ({
          ...prev,
          actualStart: value,
          actualDuration: Math.max(prev.actualDuration || 0, 1),
        }));
        return;
      }
      setNewTask((prev) => ({
        ...prev,
        [name]: value,
      }));
    };
    const handleDurationChange = (change: number) => {
      if (!newTask.plannedStart) {
        showToast({
          title: "Validation Error",
          description: "Planned Start Date must be selected first.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }
      setNewTask((prev) => {
        const newDuration = prev.duration + change;
        return {
          ...prev,
          duration: Math.max(newDuration, 0),
        };
      });
    };

    const handleDurationInputChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (!newTask.plannedStart) {
        showToast({
          title: "Validation Error",
          description: "Planned Start Date must be selected first.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      const value = e.target.value.slice(0, 6);
      const parsedValue = value.trim() === "" ? null : parseInt(value, 10);

      setNewTask((prev) => ({
        ...prev,
        duration:
          parsedValue === null || isNaN(parsedValue)
            ? 0
            : Math.max(parsedValue, 0),
      }));
    };

    const handleActualDurationChange = (change: number) => {
      if (!newTask.actualStart) {
        showToast({
          title: "Validation Error",
          description: "Actual Start Date must be selected first.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      setNewTask((prev) => {
        const newDuration = (prev.actualDuration || 0) + change;
        return {
          ...prev,
          actualDuration: Math.max(newDuration, 0),
        };
      });
    };

    const handleActualDurationInputChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (!newTask.actualStart) {
        showToast({
          title: "Validation Error",
          description: "Actual Start Date must be selected first.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      const value = e.target.value.slice(0, 6);
      const parsedValue = value.trim() === "" ? null : parseInt(value, 10);

      setNewTask((prev) => ({
        ...prev,
        actualDuration:
          parsedValue === null || isNaN(parsedValue)
            ? 0
            : Math.max(parsedValue, 0),
      }));
    };

    const handleRiskChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setNewTask((prev) => ({
        ...prev,
        risk: e.target.value,
      }));
    };

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setNewTask((prev) => ({
        ...prev,
        type: e.target.value,
      }));
    };

    const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setNewTask((prev) => ({
        ...prev,
        role: e.target.value,
      }));
    };

    const handleStageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setNewTask((prev) => ({
        ...prev,
        stage: e.target.value,
      }));
    };

    const handleStausChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newStatus = e.target.value;
      setNewTask((prev) => ({
        ...prev,
        status: newStatus,
        // Auto-update progress based on status
        progress: newStatus === "completed" ? 100 : (newStatus === "Not-started" ? 0 : prev.progress),
      }));
    };

    const handleProgressChange = (value: number) => {
      setNewTask((prev) => ({
        ...prev,
        progress: value,
      }));
    };

    const handleSliderMouseMove = (e: React.MouseEvent) => {
      const sliderRect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - sliderRect.left;
      const percentage = (x / sliderRect.width) * 100;
      setTooltipPos({
        x: e.clientX,
        y: e.clientY,
      });
      setNewTask((prev) => ({
        ...prev,
        progress: Math.min(Math.max(percentage, 0), 100),
      }));
    };

    const handleDragStart = () => {
      setIsProDragging(true);
    };

    const handleDragEnd = () => {
      setIsProDragging(false);
    };

    const handleOpenDeleteModal = () => {
      setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
      setIsDeleteModalOpen(false);
      setDeleteReason("");
    };

    useEffect(() => {
      if (!organizationId || !projectId || !channelId || !taskId) {
        console.warn("Missing required IDs to fetch tasks");
        return;
      }

      const taskDocRef = doc(
        db,
        `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
      );

      const unsubscribe = onSnapshot(
        taskDocRef,
        async (snapshot) => {
          if (snapshot.exists()) {
            const taskData = snapshot.data();

            if (taskData?.data && Array.isArray(taskData.data)) {
              const tasksData: Task[] = taskData.data.map((task: any) => ({
                id: task.id,
                name: task.name || "Untitled Task",
                plannedStart: task.plannedStart ? task.plannedStart : "",
                plannedEnd: task.plannedEnd ? task.plannedEnd : "",
                actualStart: task.actualStart ? task.actualStart : "",
                actualEnd: task.actualEnd ? task.actualEnd : "",
                duration: task.duration || 0,
                actualDuration: task.actualDuration || 0,
                dependency: task.dependency || "None",
                risk: task.risk || "Low",
                wbs: task.wbs || "N/A",
                progress: task.progress || 0,
                role: task.role || "Unknown",
                status: task.status || "Pending",
                type: task.type || "General",
                stage: task.stage || "N/A",
                assignedUsers: task.assignedUsers || [],
                parentId: task.parentId || null,
                subtasks: task.subtasks || [],
              }));

              // Sort tasks by WBS
              const sortedTasks = tasksData.sort((a, b) => {
                const wbsA = String(a.wbs).split(".").map(Number);
                const wbsB = String(b.wbs).split(".").map(Number);
                for (let i = 0; i < Math.max(wbsA.length, wbsB.length); i++) {
                  const numA = wbsA[i] || 0;
                  const numB = wbsB[i] || 0;
                  if (numA !== numB) return numA - numB;
                }
                return 0;
              });

              // For project-scoped editor access (canViewAllTasks = false),
              // filter to only show tasks/subtasks assigned to the user
              const userId = currentUser?.id || currentUser?.uid || "";
              const filteredTasks = projectScope.canViewAllTasks
                ? sortedTasks
                : sortedTasks.filter((task) => {
                    // Keep the task if user created it, is assigned to it, or any subtask is assigned
                    if (task.createdBy === userId) return true;
                    if (task.assignedUsers?.includes(userId)) return true;
                    if (task.subtasks?.some(
                      (st) => st.createdBy === userId || st.assignedUsers?.includes(userId)
                    )) return true;
                    return false;
                  }).map((task) => ({
                    ...task,
                    subtasks: filterTaskDataByAssignment(task.subtasks as any, userId) as any,
                  }));

              setTask(filteredTasks as Task[]);

              // Auto-mark delayed tasks - logic kept but wrapped to avoid unnecessary updates
              try {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const isPast = (dateStr: string | undefined) => {
                  if (!dateStr) return false;
                  const d = new Date(dateStr);
                  d.setHours(0, 0, 0, 0);
                  return d < today;
                };

                const markDelays = (tasks: Task[]): { updated: Task[]; changed: boolean } => {
                  let changed = false;
                  const updated = tasks.map((t) => {
                    const shouldDelay = t.status?.toLowerCase() !== "completed" && (isPast(t.actualEnd) || isPast(t.plannedEnd));
                    const base: Task = { ...t, status: shouldDelay ? "delay" : t.status };
                    if (shouldDelay && t.status !== "delay") changed = true;
                    if (t.subtasks && t.subtasks.length > 0) {
                      const sub = markDelays(t.subtasks);
                      if (sub.changed) changed = true;
                      base.subtasks = sub.updated;
                    }
                    return base;
                  });
                  return { updated, changed };
                };

                const { updated, changed } = markDelays(sortedTasks);
                if (changed) {
                  // Wait a bit before updating to avoid too many writes if multiple tasks are delayed
                  await updateDoc(taskDocRef, { data: updated });
                }
              } catch (e) {
                console.warn("Auto-delay status update skipped:", e);
              }
            }
          } else {
            console.warn("No document found for the selected task.");
          }
        },
        (error) => {
          console.error("Error fetching tasks in real-time:", error);
        }
      );

      return () => unsubscribe();
    }, [organizationId, projectId, channelId, taskId]);

    const handleSaveTask = async () => {
      // Validation checks
      if (
        !newTask.name ||
        !newTask.plannedStart ||
        !newTask.plannedEnd ||
        !newTask.type ||
        !newTask.stage ||
        !newTask.status ||
        !newTask.risk
      ) {
        showToast({
          title: "Validation Error",
          description: "Please fill in all required fields.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (new Date(newTask.plannedEnd) < new Date(newTask.plannedStart)) {
        showToast({
          title: "Validation Error",
          description:
            "Planned End date must be greater than or equal to Planned Start date.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setIsSavingOrUpdating(true); // Set loading state to true

      try {
        const taskRef = doc(
          db,
          `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
        );

        const docSnapshot = await getDoc(taskRef);

        // Fetch existing data
        let updatedData = docSnapshot.exists()
          ? docSnapshot.data().data || []
          : [];

        const existingLinks = docSnapshot.data()?.links || [];
        const existingAudits = docSnapshot.data()?.taskAudits || {};

        // Check if the task is a subtask
        if (newTask.parentId && newTask.parentId !== "0") {
          // Find and update the parent task
          const updateParentTask = (tasks: Task[], parentId: string): Task[] =>
            tasks.map((task) =>
              task.id === parentId
                ? {
                  ...task,
                  subtasks: [...(task.subtasks || []), { ...newTask }],
                }
                : {
                  ...task,
                  subtasks: task.subtasks
                    ? updateParentTask(task.subtasks, parentId)
                    : [],
                }
            );

          updatedData = updateParentTask(updatedData, newTask.parentId);
        } else {
          // Add the new task to the top level
          updatedData.push({
            ...newTask,
            subtasks: [],
          });
        }

        // Save the updated data back to Firestore with merge option
        await setDoc(
          taskRef,
          {
            container: projectId,
            data: updatedData,
            links: existingLinks, // Preserve existing links
            taskAudits: existingAudits, // Preserve existing audits
          },
          { merge: true } // Preserve old data and merge new updates
        );

        if (newTask.assignedUsers && newTask.assignedUsers.length > 0) {
          for (const userId of newTask.assignedUsers) {
            const userRef = doc(db, "users", userId);
            const userDoc = await getDoc(userRef);

            if (userDoc.exists()) {
              const user = userDoc.data();
              await updateDoc(userRef, {
                assignedTasks: arrayUnion({
                  id: newTask.id,
                  projectId: projectId,
                  taskId: taskId,
                  channelId: channelId,
                  name: newTask.name, // ✅ Store both task ID and name
                }), // Add task ID to assignedTasks
              });

              // Add notification for task assignment
              await setDoc(
                doc(
                  db,
                  `organization/${organizationId}/notifications/${userId}`
                ),
                {
                  messages: arrayUnion({
                    message: `You've been assigned to task "${newTask.name}" in project "${ProjectName}".`,
                    projectId,
                    projectName: ProjectName,
                    taskId: newTask.id,
                    taskName: newTask.name,
                    channelId,
                    assignedBy: "system",
                    timestamp: new Date(),
                    read: false,
                  }),
                },
                { merge: true }
              );

              await axios.post(`${backendUrl}/api/email/task-assigned`, {
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                taskName: newTask.name,
                plannedStart: newTask.plannedStart,
                plannedEnd: newTask.plannedEnd,
              });
            } else {
              console.error(`User with ID ${userId} not found.`);
            }
          }
        }

        // ✅ Update organization members
        const orgRef = doc(db, "organization", organizationId);
        const orgDoc = await getDoc(orgRef);

        if (orgDoc.exists()) {
          const orgData = orgDoc.data();
          const updatedMembers = orgData.members.map((member: any) => {
            if (newTask.assignedUsers?.includes(member.id)) {
              const newTaskEntry = {
                id: newTask.id,
                name: newTask.name,
                projectId: projectId,
                taskId: taskId,
                channelId: channelId,
              };
              return {
                ...member,
                assignedTasks: member.assignedTasks
                  ? [
                    ...Array.from(new Set([
                      ...member.assignedTasks.map((task: any) =>
                        JSON.stringify(task)
                      ),
                      JSON.stringify(newTaskEntry),
                    ])),
                  ].map((task) => JSON.parse(task)) // ✅ Prevent duplicates
                  : [newTaskEntry],
              };
            }
            return member;
          });

          await updateDoc(orgRef, { members: updatedMembers });
        }
        // Update local state
        setTask(updatedData);

        showToast({
          title: "Task Saved",
          description: `Task "${newTask.name}" has been saved to project.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Log task creation activity
        try {
          const currentUserAuth = auth.currentUser;
          if (currentUserAuth && organizationId) {
            const userRef = doc(db, "users", currentUserAuth.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const userData = userSnap.data();
              const taskType = newTask.parentId && newTask.parentId !== "0" ? "Subtask" : "Task";
              await logToSystem(organizationId, "actions", {
                type: "ACTIVITY",
                user: `${userData.firstName} ${userData.lastName}`,
                email: userData.email,
                action: "Task Management",
                details: `Created new ${taskType} ${newTask.name} in ${TaskName} for project ${ProjectName}`,
                status: "success",
              });
            }
          }
        } catch (logError) {
          console.error("Error logging task creation:", logError);
        }

        setIsModalOpen(false);
      } catch (error: any) {
        console.error("Error saving task:", error);
        showToast({
          title: "Error",
          description: `Failed to save task: ${error.message}`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsSavingOrUpdating(false); // Set loading state to false regardless of outcome
      }
    };

    const handleUpdateTask = async () => {
      // Validation checks
      if (
        !newTask.name ||
        !newTask.plannedStart ||
        !newTask.plannedEnd ||
        !newTask.type ||
        !newTask.stage ||
        !newTask.status ||
        !newTask.risk
      ) {
        showToast({
          title: "Validation Error",
          description: "All fields are required.",
          status: "warning",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      // if (new Date(newTask.actualStart) < new Date(newTask.plannedStart)) {
      //   showToast({
      //     title: "Validation Error",
      //     description:
      //       "Actual Start date cannot be earlier than Planned Start date.",
      //     status: "error",
      //     duration: 3000,
      //     isClosable: true,
      //     position: "top",
      //   });
      //   return;
      // }

      if (new Date(newTask.plannedEnd) < new Date(newTask.plannedStart)) {
        showToast({
          title: "Validation Error",
          description:
            "Planned End date must be greater than or equal to Planned Start date.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      if (!editingTaskId) {
        showToast({
          title: "Error",
          description: "No task selected for update.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      setIsSavingOrUpdating(true); // Set loading state to true

      try {
        const projectRef = doc(
          db,
          `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
        );
        const docSnapshot = await getDoc(projectRef);

        if (!docSnapshot.exists()) {
          throw new Error("Project not found");
        }

        // Get current project data and audits
        const currentData = docSnapshot.data().data || [];
        const currentAudits = docSnapshot.data().TaskAudits || {};

        // Helper function to find the task being updated
        const findTaskById = (
          tasks: Task[],
          taskId: string
        ): Task | undefined => {
          for (const task of tasks) {
            if (task.id === taskId) return task;
            if (task.subtasks && task.subtasks.length > 0) {
              const foundTask = findTaskById(task.subtasks, taskId);
              if (foundTask) return foundTask;
            }
          }
          return undefined;
        };

        const existingTask = findTaskById(currentData, editingTaskId);
        if (!existingTask) {
          throw new Error("Task not found in the hierarchy");
        }

        const previousAssignedUsers = existingTask.assignedUsers || [];
        const newAssignedUsers = newTask.assignedUsers || [];

        const addedUsers = newAssignedUsers.filter(
          (userId) => !previousAssignedUsers.includes(userId)
        );
        const removedUsers = previousAssignedUsers.filter(
          (userId) => !newAssignedUsers.includes(userId)
        );

        // Send notifications for newly assigned users
        for (const userId of addedUsers) {
          await setDoc(
            doc(db, `organization/${organizationId}/notifications/${userId}`),
            {
              messages: arrayUnion({
                message: `You've been assigned to task "${newTask.name}" in project "${ProjectName}".`,
                projectId,
                projectName: ProjectName || "N/A",
                taskId: newTask.id,
                taskName: newTask.name,
                channelId,
                assignedBy: "system",
                timestamp: new Date(),
                read: false,
              }),
            },
            { merge: true }
          );
        }

        // Send assignment emails ONLY to newly added users
        for (const userId of addedUsers) {
          try {
            const userRef = doc(db, "users", userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const u = userSnap.data();
              await axios.post(`${backendUrl}/api/email/task-assigned`, {
                email: u.email,
                firstName: u.firstName,
                lastName: u.lastName,
                taskName: newTask.name,
                plannedStart: newTask.plannedStart,
                plannedEnd: newTask.plannedEnd,
              });
            }
          } catch (e) {
            console.warn("Failed to email newly assigned user", userId, e);
          }
        }

        // Send notifications for unassigned users
        for (const userId of removedUsers) {
          await setDoc(
            doc(db, `organization/${organizationId}/notifications/${userId}`),
            {
              messages: arrayUnion({
                message: `You've been unassigned from task "${newTask.name}" in project "${ProjectName}".`,
                projectId,
                projectName: ProjectName || "N/A",
                taskId: newTask.id,
                taskName: newTask.name,
                channelId,
                assignedBy: "system",
                timestamp: new Date(),
                read: false,
              }),
            },
            { merge: true }
          );
        }

        // Send notifications to all assigned users about task updates
        const allAssignedUsers = Array.from(new Set([...newAssignedUsers]));
        for (const userId of allAssignedUsers) {
          await setDoc(
            doc(db, `organization/${organizationId}/notifications/${userId}`),
            {
              messages: arrayUnion({
                message: `Task "${newTask.name}" has been updated in project "${ProjectName}".`,
                projectId,
                projectName: ProjectName || "N/A",
                taskId: newTask.id,
                taskName: newTask.name,
                channelId,
                assignedBy: "system",
                timestamp: new Date(),
                read: false,
              }),
            },
            { merge: true }
          );
        }

        // ✅ Update Firestore for assigned users
        for (const userId of addedUsers) {
          const userRef = doc(db, "users", userId);
          await updateDoc(userRef, {
            assignedTasks: arrayUnion({
              id: taskId,
              name: newTask.name,
              projectId: projectId,
              taskId: taskId,
              channelId: channelId,
            }),
          });
        }

        for (const userId of removedUsers) {
          const userRef = doc(db, "users", userId);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const updatedTasks = (userData.assignedTasks || []).filter(
              (task: any) => task.id !== taskId // ✅ Remove by taskId
            );

            await updateDoc(userRef, {
              assignedTasks: updatedTasks,
            });
          }
        }

        // ✅ Update organization members
        const orgRef = doc(db, "organization", organizationId);
        const orgDoc = await getDoc(orgRef);

        if (orgDoc.exists()) {
          const orgData = orgDoc.data();
          const updatedMembers = orgData.members.map((member: any) => {
            if (addedUsers.includes(member.id)) {
              const newTasks = { id: newTask.id, name: newTask.name, projectId: projectId, taskId: taskId, channelId: channelId };
              const existingTasks = member.assignedTasks || [];

              // ✅ Prevent duplicate tasks
              const isTaskAlreadyAssigned = existingTasks.some(
                (task: any) => task.id === taskId
              );

              return {
                ...member,
                assignedTasks: isTaskAlreadyAssigned
                  ? existingTasks
                  : [...existingTasks, newTasks],
              };
            } else if (removedUsers.includes(member.id)) {
              return {
                ...member,
                assignedTasks: (member.assignedTasks || []).filter(
                  (task: any) => task.id !== newTask.id // ✅ Remove by taskId
                ),
              };
            }
            return member;
          });

          await updateDoc(orgRef, { members: updatedMembers });
        }

        // Calculate the differences
        const isEqual = (a: any, b: any): boolean => {
          // Handle cases where a or b is undefined/null
          if (a === null || a === undefined || b === null || b === undefined) {
            return a === b;
          }

          // Handle primitive types
          if (typeof a !== "object" || typeof b !== "object") {
            return a === b;
          }

          // Check if arrays
          if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false; // Different lengths
            return a.every((item, index) => isEqual(item, b[index])); // Compare each item
          }

          // Handle objects (deep comparison for keys and values)
          const aKeys = Object.keys(a);
          const bKeys = Object.keys(b);

          if (aKeys.length !== bKeys.length) return false;

          return aKeys.every((key) => isEqual(a[key], b[key]));
        };

        // Calculate differences
        const changes: any[] = [];
        Object.keys(newTask).forEach((key) => {
          const keyName = key as keyof Task; // Type assertion for keyof Task

          // Normalize values for comparison
          const oldValue =
            keyName === "progress"
              ? Math.round(existingTask[keyName] as number) // Round progress
              : existingTask[keyName];

          const newValue =
            keyName === "progress"
              ? Math.round(newTask[keyName] as number) // Round progress
              : newTask[keyName];

          // Compare the normalized values
          if (!isEqual(newValue, oldValue)) {
            changes.push({
              field: keyName,
              previousValue: oldValue, // Use normalized value
              newValue: newValue, // Use normalized value
              updatedAt: new Date().toISOString(),
            });
          }
        });

        // Update audits for this task
        if (!currentAudits[editingTaskId]) {
          currentAudits[editingTaskId] = []; // Initialize if it doesn't exist
        }
        currentAudits[editingTaskId] = [
          ...currentAudits[editingTaskId],
          ...changes, // Append changes
        ];

        // Update task in the hierarchy
        const updateTaskInHierarchy = (tasks: Task[], taskId: string): Task[] =>
          tasks.map((task) =>
            task.id === taskId
              ? { ...task, ...newTask }
              : {
                ...task,
                subtasks: updateTaskInHierarchy(task.subtasks || [], taskId),
              }
          );

        const updatedData = updateTaskInHierarchy(currentData, editingTaskId);

        // Save back to Firestore
        await updateDoc(projectRef, {
          [`data`]: updatedData, // Update the task hierarchy
          [`taskAudits.${editingTaskId}`]: arrayUnion(
            ...changes.map((change, index) => ({
              id: currentAudits[editingTaskId].length + index, // Incremental ID
              field: change.field,
              previousValue: change.previousValue,
              newValue: change.newValue,
              updatedAt: change.updatedAt,
            }))
          ),
        });

        setTask(updatedData);

        showToast({
          title: "Task Updated",
          description: `Task "${newTask.name}" has been successfully updated.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Log task update activity
        try {
          const currentUserAuth = auth.currentUser;
          if (currentUserAuth && organizationId) {
            const userRef = doc(db, "users", currentUserAuth.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const userData = userSnap.data();
              const taskType = newTask.parentId && newTask.parentId !== "0" ? "Subtask" : "Task";
              const changeList = changes.map(c => `${c.field}: ${c.previousValue} -> ${c.newValue}`).join(", ");
              await logToSystem(organizationId, "actions", {
                type: "ACTIVITY",
                user: `${userData.firstName} ${userData.lastName}`,
                email: userData.email,
                action: "Task Management",
                details: `Updated ${taskType} ${newTask.name} in ${TaskName} for project ${ProjectName}. Changes: ${changeList || "No changes"}`,
                status: "success",
              });
            }
          }
        } catch (logError) {
          console.error("Error logging task update:", logError);
        }

        setEditingTaskId(null);
        setIsModalOpen(false);
      } catch (error: any) {
        console.error("Error updating task:", error);
        showToast({
          title: "Error",
          description: `Failed to update task: ${error.message}`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsSavingOrUpdating(false); // Set loading state to false regardless of outcome
      }
    };

    const handleDeleteTask = async (reason: string) => {
      if (!editingTaskId) {
        showToast({
          title: "Error",
          description: "No task selected for deletion.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        return;
      }

      try {
        const projectRef = doc(
          db,
          `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
        );
        const docSnapshot = await getDoc(projectRef);

        if (!docSnapshot.exists()) {
          throw new Error("Project not found");
        }

        // Get current project data and audits
        const currentData = docSnapshot.data().data || [];
        const currentAudits = docSnapshot.data().taskAudits || {}; // Audits grouped by Task ID

        // Find the task being deleted
        const findTaskById = (tasks: Task[], taskId: string): Task | null => {
          for (const task of tasks) {
            if (task.id === taskId) return task;
            if (task.subtasks && task.subtasks.length > 0) {
              const found = findTaskById(task.subtasks, taskId);
              if (found) return found;
            }
          }
          return null;
        };

        const deletedTask = findTaskById(currentData, editingTaskId);

        if (!deletedTask) {
          throw new Error("Task not found in hierarchy");
        }

        // Prepare deletion audit entry
        const deletionEntry = {
          id: (currentAudits[editingTaskId]?.length || 0) + 1, // Increment ID within the same task audit
          field: "deletion",
          previousValue: null,
          newValue: {
            taskName: deletedTask.name,
            role: deletedTask.role,
            reason,
          },
          updatedAt: new Date().toISOString(),
        };

        // Append the deletion audit under the same Task ID
        if (!currentAudits[editingTaskId]) {
          currentAudits[editingTaskId] = []; // Initialize if missing
        }

        currentAudits[editingTaskId] = [
          ...currentAudits[editingTaskId], // Preserve previous audits
          deletionEntry, // Add new deletion audit
        ];

        // Remove task from hierarchy
        const removeTaskFromHierarchy = (
          tasks: Task[],
          taskId: string
        ): Task[] =>
          tasks
            .filter((task) => task.id !== taskId) // Remove the matching task
            .map((task) => ({
              ...task,
              subtasks: removeTaskFromHierarchy(task.subtasks || [], taskId), // Recursively remove subtasks
            }));

        const updatedData = removeTaskFromHierarchy(currentData, editingTaskId);
        if (deletedTask.assignedUsers && deletedTask.assignedUsers.length > 0) {
          for (const userId of deletedTask.assignedUsers) {
            const userRef = doc(db, "users", userId);
            const userDoc = await getDoc(userRef);

            if (userDoc.exists()) {
              const userData = userDoc.data();
              const updatedTasks = (userData.assignedTasks || []).filter(
                (task: any) => task.id !== newTask.id // ✅ Remove task by ID
              );

              await updateDoc(userRef, {
                assignedTasks: updatedTasks,
              });

              // Add notification for task deletion
              await setDoc(
                doc(
                  db,
                  `organization/${organizationId}/notifications/${userId}`
                ),
                {
                  messages: arrayUnion({
                    message: `Task "${deletedTask.name}" has been deleted from project "${ProjectName}".`,
                    projectId,
                    projectName: ProjectName,
                    taskId: deletedTask.id,
                    taskName: deletedTask.name,
                    channelId,
                    assignedBy: "system",
                    timestamp: new Date(),
                    read: false,
                  }),
                },
                { merge: true }
              );
            }
          }
        }

        // ✅ Remove the task from organization members
        const orgRef = doc(db, "organization", organizationId);
        const orgDoc = await getDoc(orgRef);

        if (orgDoc.exists()) {
          const orgData = orgDoc.data();
          const updatedMembers = orgData.members.map((member: any) => {
            if (deletedTask.assignedUsers?.includes(member.id)) {
              return {
                ...member,
                assignedTasks: (member.assignedTasks || []).filter(
                  (task: any) => task.id !== newTask.id // ✅ Remove task by ID
                ),
              };
            }
            return member;
          });

          await updateDoc(orgRef, { members: updatedMembers });
        }
        // Save updated data and audits to Firestore
        await setDoc(
          projectRef,
          {
            data: updatedData,
            taskAudits: currentAudits, // Store audits under the same task ID
          },
          { merge: true } // Preserve existing data while updating
        );

        // Update local state
        setTask(updatedData);

        showToast({
          title: "Task Deleted",
          description: `Task "${deletedTask.name}" has been successfully deleted.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Log task deletion activity
        try {
          const currentUserAuth = auth.currentUser;
          if (currentUserAuth && organizationId) {
            const userRef = doc(db, "users", currentUserAuth.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const userData = userSnap.data();
              const taskType = deletedTask.parentId && deletedTask.parentId !== "0" ? "Subtask" : "Task";
              await logToSystem(organizationId, "actions", {
                type: "ACTIVITY",
                user: `${userData.firstName} ${userData.lastName}`,
                email: userData.email,
                action: "Task Management",
                details: `Deleted ${taskType} ${deletedTask.name} from ${TaskName} in project ${ProjectName}. Reason: ${reason}`,
                status: "success",
              });
            }
          }
        } catch (logError) {
          console.error("Error logging task deletion:", logError);
        }

        setEditingTaskId(null);
        setIsModalOpen(false);
      } catch (error: any) {
        console.error("Error deleting task:", error);
        showToast({
          title: "Error",
          description: `Failed to delete task: ${error.message}`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };
    const handleViewAudits = async (viewtaskId: string, taskName: string) => {
      // Replace with dynamic ID if required

      console.log("taskId", viewtaskId, "taskName", taskName);

      console.log("View edits called");
      try {
        const projectRef = doc(
          db,
          `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
        );
        const docSnapshot = await getDoc(projectRef);

        console.log("docSnap shot", docSnapshot);

        if (docSnapshot.exists()) {
          const audits = docSnapshot.data().taskAudits || {}; // Fetch all audits

          // Get audits for the specific task
          const taskAudits = audits[viewtaskId] || [];

          console.log("taskAudits", taskAudits);

          const filteredAudits = taskAudits.filter(
            (audit: any) => audit.field !== "deletion" // Exclude deletion field
          );

          setSelectedAudit(filteredAudits); // Update the selected audit data
          setSelectedTaskName(taskName); // Update the task name for display
          setIsAuditModalOpen(true); // Open the modal
          console.log("end");
        }
      } catch (error) {
        console.error("Error fetching audits:", error);
        showToast({
          title: "Error",
          description: "Failed to fetch audit details.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    const fetchDeletedAudits = async () => {
      const projectRef = doc(
        db,
        `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
      );
      const docSnapshot = await getDoc(projectRef);

      if (docSnapshot.exists()) {
        const audits = docSnapshot.data().taskAudits || {};

        const deletedAudits: any[] = [];

        // Loop through each task ID in audits
        Object.keys(audits).forEach((taskId) => {
          audits[taskId].forEach((audit: any) => {
            if (audit.field === "deletion") {
              deletedAudits.push({
                taskId,
                taskName: audit.newValue.taskName || "Untitled Task",
                role: audit.newValue.role || "N/A",
                reason: audit.newValue.reason || "No reason provided",
                updatedAt: audit.updatedAt,
              });
            }
          });
        });

        deletedAudits.sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );

        // Update state with deleted audits
        setDeletedAudits(deletedAudits);
        setIsDeletedAuditModalOpen(true);
      } else {
        console.error("No audits found.");
      }
    };

    const handlePrintClick = async (): Promise<void> => {
      // Log the print action
      try {
        const currentUserAuth = auth.currentUser;
        if (currentUserAuth && organizationId) {
          const userRef = doc(db, "users", currentUserAuth.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            await logToSystem(organizationId, "actions", {
              type: "ACTIVITY",
              user: `${userData.firstName} ${userData.lastName}`,
              email: userData.email,
              action: "Task Management",
              details: `Printed ${tasks.length} tasks/subtasks from ${TaskName} for project ${ProjectName}`,
              status: "success",
            });
          }
        }
      } catch (logError) {
        console.error("Error logging print:", logError);
      }

      // Perform the print action
      window.print();
    };

    const getRowBackground = (
      status: string | undefined,
      isSelected: boolean
    ): string => {
      if (isSelected) return "purple.50";
      const normalized = (status || "").toLowerCase();
      if (normalized === "completed") return "green.50";
      if (normalized === "delay") return "red.50";
      return "white";
    };

    const renderTaskRows = (task: Task, level = 0): React.ReactNode => (
      <React.Fragment key={`task-${task.id}`}>
        <Tr
          height="50px"
          fontSize="xs"
          style={{ height: "40px" }}
          key={task.id}
          color={"black"}
          bg={getRowBackground(task.status, selectedTaskId === task.id)}
          onClick={() => onSelectTask(task.id)}
          onDoubleClick={() => handleRowDoubleClick(task)}
          _hover={{ bg: "gray.100", cursor: "pointer" }}
        >
          <Td
            borderColor="gray.200"
            borderRightWidth="1px"
            textAlign="center"
            height="60px"
          >
            {task.wbs}
          </Td>
          {canCreateTask && (
            <Td
              borderColor={isDark ? "whiteAlpha.200" : "gray.200"}
              borderRightWidth="1px"
              textAlign="center"
              minWidth="20px"
              cursor="pointer"
              color="blue.500"
              _hover={{ color: "blue.600", transform: "scale(1.3)" }}
            >
              <FontAwesomeIcon
                icon={faPlus}
                onClick={() => handleSubtaskAddClick(task.id)}
                size="sm"
              />
            </Td>
          )}

          <Td
            borderColor={isDark ? "whiteAlpha.200" : "gray.200"}
            borderRightWidth="1px"
            textAlign="center"
            height="60px"
            onClick={() => toggleExpandedTask(task.id)}
            cursor="pointer"
            paddingLeft={`calc(10px + ${level * 20}px)`}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
            >
              {task.type === "projects" ? (
                <>
                  <FontAwesomeIcon
                    icon={
                      expandedTaskIds.has(task.id) ? faMinusSquare : faPlusSquare
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpandedTask(task.id);
                    }}
                    style={{
                      marginRight: "8px",
                      cursor: "pointer",
                      color: expandedTaskIds.has(task.id) ? "red" : "green", // Conditional color
                    }}
                  />

                  <FontAwesomeIcon
                    color={level === 0 ? "orange" : "blue"}
                    icon={expandedTaskIds.has(task.id) ? faFolderOpen : faFolder}
                    style={{ marginRight: "8px" }}
                  />
                </>
              ) : (
                <FontAwesomeIcon
                  icon={faFileAlt}
                  color="blue"
                  style={{ marginRight: "8px" }}
                />
              )}

              <Tooltip label={task.name} hasArrow placement="top" openDelay={300}>
                <Box
                  as="span"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  maxWidth="230px"
                  textAlign={"left"}
                  display={"flex"}
                >
                  {task.subtasks && task.subtasks.length > 0 && (
                    <IconWrapper
                      icon={FiChevronRight}
                      size={15}
                      style={{
                        marginTop: 4,
                        marginRight: 2,
                        fontWeight: "bold",
                        color: "purple",
                        transition: "transform 120ms ease",
                        transform: expandedTaskIds.has(task.id)
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                      }}
                    />
                  )}
                  {task.name}
                </Box>
              </Tooltip>
            </Box>
          </Td>

          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            {formatDateForDisplay(task.plannedStart)}
          </Td>
          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            {formatDateForDisplay(task.plannedEnd)}
          </Td>
          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            {formatDateForDisplay(task.actualStart)}
          </Td>
          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            {formatDateForDisplay(task.actualEnd)}
          </Td>
          {/* <Td
            borderColor="gray.200"
            borderRightWidth="1px"
            textAlign="center"
            height="60px"
          >
            {task.type === "task" ? `${task.stage}` : ""}
          </Td> */}
          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            {task.duration}
          </Td>
          {/* <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            {task.dependency}
          </Td> */}
          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            {task.actualDuration}
          </Td>
          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            {task.status}
          </Td>

          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            {task.risk}
          </Td>
          <Td
            height="60px"
            borderColor="gray.200"
            borderRightWidth="1px"
            textAlign="center"
          >
            <AssignedUsersPopover
              color={color}
              bg={bg}
              assignedUsers={(task.assignedUsers ?? []).filter((id) =>
                users.some((u) => u.id === id)
              )}
              usersData={users}
            />
          </Td>
          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            <Box width="100%">
              <Progress
                height="5px"
                width={`${task.progress}%`}
                backgroundColor={
                  task.progress < 30
                    ? "red.400"
                    : task.progress < 70
                      ? "yellow.400"
                      : "green.400"
                }
                borderRadius="md"
              />
              <Text
                mt={1}
                fontSize="xs"
                fontWeight="bold"
                color={
                  isDark
                    ? task.progress < 30
                      ? "red.300"
                      : task.progress < 70
                        ? "yellow.300"
                        : "green.300"
                    : task.progress < 30
                      ? "red.600"
                      : task.progress < 70
                        ? "yellow.600"
                        : "green.600"
                }
              >
                {`${Math.round(task.progress)}%`}
              </Text>
            </Box>
          </Td>

          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            <Button
              variant="gold"
              size="xs"
              onClick={() => handleViewAudits(task.id, task.name)} // Pass taskId and name
            >
              View
            </Button>
          </Td>
          <Td borderColor="gray.200" borderRightWidth="1px" textAlign="center">
            {task.predecessor && task.predecessor.length > 0
              ? task.predecessor.join(", ")
              : ""}
          </Td>
        </Tr>
        {/* Render Subtasks if the task is expanded */}
        {expandedTaskIds.has(task.id) &&
          task.subtasks &&
          task.subtasks.map((subtask) => renderTaskRows(subtask, level + 1))}
      </React.Fragment>
    );

    const handleUserSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedUserId = e.target.value;

      // ✅ Use optional chaining with fallback
      if (
        selectedUserId &&
        !(newTask.assignedUsers ?? []).includes(selectedUserId)
      ) {
        setNewTask((prev) => ({
          ...prev,
          assignedUsers: [...(prev.assignedUsers ?? []), selectedUserId],
        }));
      }
    };

    const handleRemoveUser = (userId: string) => {
      setNewTask((prev) => ({
        ...prev,
        assignedUsers: (prev.assignedUsers ?? []).filter((id) => id !== userId),
      }));
    };

    useImperativeHandle(ref, () => ({
      handleAddTaskClick,
      handleExportClick,
      handleImportClick,
      handleDownloadTemplate,
      fetchDeletedAudits,
      handlePrintClick,
    }));



    return (
      <>
      <Box
        className="gantt-print-container"
        width={width}
        p={2}
        overflowY="auto"
        overflowX={"auto"}
        height={isFullscreen ? "95vh" : "85vh"}
      >
        <Text className="print-header">
          {TaskName}
        </Text>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".xlsx, .xls"
          onChange={handleFileChange}
        />
        {/* <Flex gap={5}>
        <Text fontSize="xl" fontWeight="bold" mb={4} minWidth={"150px"}>
          {TaskName}
        </Text>
        <Button
          onClick={() => handleAddTaskClick()}
          bg="teal.500"
          color="white"
          size="sm"
          fontWeight="bold"
          borderRadius="md"
          minWidth={"100px"}
          _hover={{
            bg: "teal.600",
            transform: "scale(1.05)",
          }}
          _active={{
            bg: "teal.700",
            transform: "scale(1.02)",
          }}
        >
          + Add Task
        </Button>

        <Button
          colorScheme="red"
          color="white"
          size="sm"
          fontWeight="bold"
          borderRadius="md"
          minWidth={"120px"}
          onClick={fetchDeletedAudits}
        >
          Deleted Audits
        </Button>

        <Button
          colorScheme="green"
          color="white"
          size="sm"
          fontWeight="bold"
          borderRadius="md"
          minWidth={"100px"}
          onClick={handleExportClick}
        >
          Export
        </Button>

        <Button
          colorScheme="green"
          color="white"
          size="sm"
          fontWeight="bold"
          borderRadius="md"
          minWidth={"100px"}
          onClick={handlePrintClick}
        >
          Print
        </Button>
      </Flex> */}

        <Table
          border={"1px"}
          borderColor="gray.200"
          variant="simple"
          colorScheme="gray"
        >
          <Thead
            fontSize={"sm"}
            position={"sticky"}
            top={"-7px"}
            bg="#FFF7E6"
            zIndex={2}
          >
            <Tr top={"5px"}>
              <Th
                fontSize={"10px"}
                borderColor="gray.100"
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"50px"}
                color={undefined}
              >
                WBS
              </Th>
              {canCreateTask && (
                <Th
                  borderColor="gray.100"
                  borderRightWidth="1px"
                  textAlign="center"
                  minWidth="20px"
                  cursor="pointer"
                  color={isDark ? "#FBD282" : "#B59143"}
                  _hover={{ color: isDark ? "#E8C16C" : "#A7843D", transform: "scale(1.2)" }}
                >
                  <FontAwesomeIcon
                    onClick={() => handleAddTaskClick()}
                    icon={faPlus}
                    size="lg"
                  />
                </Th>
              )}

              <Th
                fontSize={"10px"}
                borderColor="gray.100"
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"200px"}
                height={`${zoomLevel === "weeks"
                  ? "39px"
                  : `${zoomLevel === "days" ? "21px" : "83px"}`
                  }`}
                color={undefined}
              >
                Task Name
              </Th>

              <Th
                fontSize={"10px"}
                borderColor="gray.100"
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"140px"}
                color={undefined}
              >
                Planned Start
              </Th>
              <Th
                fontSize={"10px"}
                borderColor="gray.100"
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"140px"}
                color={undefined}
              >
                Planned End
              </Th>
              <Th
                fontSize={"10px"}
                borderColor="gray.100"
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"140px"}
                color={undefined}
              >
                Actual Start
              </Th>
              <Th
                fontSize={"10px"}
                borderColor="gray.100"
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"140px"}
                color={undefined}
              >
                Actual End
              </Th>
              {/* <Th
                fontSize={"10px"}
                borderColor={"gray.100"}
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"60px"}
              >
                Stage
              </Th> */}
              <Th
                fontSize={"10px"}
                borderColor="gray.100"
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"80px"}
                color={undefined}
              >
                Duration
              </Th>
              {/* <Th
                    fontSize={"10px"}
                borderColor={"gray.200"}
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"150px"}
              >
                Dependency
              </Th> */}
              <Th
                fontSize={"10px"}
                borderColor="gray.100"
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"120px"}
                color={undefined}
              >
                Actual Duration
              </Th>
              <Th
                fontSize={"10px"}
                borderColor="gray.100"
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"120px"}
                color={undefined}
              >
                Status
              </Th>

              <Th
                fontSize={"10px"}
                borderColor="gray.100"
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"80px"}
                color={undefined}
              >
                Risk
              </Th>
              <Th
                fontSize={"10px"}
                borderColor={isDark ? "#2E2F33" : "gray.100"}
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"80px"}
                color={isDark ? "white" : undefined}
              >
                Users
              </Th>
              <Th
                fontSize={"10px"}
                borderColor={isDark ? "#2E2F33" : "gray.100"}
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"90px"}
                color={isDark ? "white" : undefined}
              >
                Progress
              </Th>

              <Th
                fontSize={"10px"}
                borderColor={isDark ? "#2E2F33" : "gray.100"}
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"80px"}
                color={isDark ? "white" : undefined}
              >
                Audits
              </Th>
              <Th
                fontSize={"10px"}
                borderColor={isDark ? "#2E2F33" : "gray.100"}
                borderRightWidth="1px"
                textAlign="center"
                minWidth={"100px"}
                color={isDark ? "white" : undefined}
              >
                Predecessors
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {tasks.length > 0 ? (
              tasks.map((task) => renderTaskRows(task))
            ) : (
              <Tr>
                <Td
                  colSpan={7}
                  textAlign="center"
                  fontWeight="bold"
                  color={isDark ? "#C3C9D3" : "gray.500"}
                >
                  No tasks available
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>

        <Modal
          isOpen={isDeletedAuditModalOpen}
          onClose={() => setIsDeletedAuditModalOpen(false)}
          size="xl"
          closeOnOverlayClick={false}
          closeOnEsc={true}
          portalProps={
            isFullscreen ? { containerRef: fullscreenContainerRef } : undefined
          }
        >
          <ModalOverlay />
          <ModalContent bg={isDark ? "#1A1C1F" : "white"} borderWidth="1px" borderColor={isDark ? "#2E2F33" : "gray.200"}>
            <ModalHeader color={isDark ? "white" : "#111827"} bgGradient={"linear(to-r, #FBD282, #B59143)"}>Deleted Task Audits</ModalHeader>
            <ModalCloseButton color={isDark ? "white" : "#111827"} />
            <ModalBody color={isDark ? "white" : "#111827"}>
              {deletedAudits.length > 0 ? (
                <Box maxHeight="400px" overflowY="auto">
                  {deletedAudits.map((audit, index) => (
                    <Box
                      key={index}
                      borderWidth="1px"
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="sm"
                      bg={isDark ? "#14161B" : "white"}
                      borderColor={isDark ? "#2E2F33" : "gray.200"}
                      p={4}
                      mb={4}
                    >
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        mb={3}
                        color={isDark ? "white" : "#111827"}
                      >
                        <Heading size="sm">
                          {audit.taskName || "Untitled Task"}
                        </Heading>
                        <Badge colorScheme="red" px={2} py={1}>
                          Deleted
                        </Badge>
                      </Flex>

                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        <b>Role:</b> {audit.role || "N/A"}
                      </Text>

                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        <b>Reason:</b> {audit.reason || "No reason provided"}
                      </Text>

                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        <b>Deleted At:</b>{" "}
                        {new Date(audit.updatedAt).toLocaleString()}
                      </Text>

                      <Button
                        variant="gold"
                        size="sm"
                        onClick={() =>
                          handleViewAudits(audit.taskId, audit.taskName)
                        } // Pass task ID and name
                      >
                        View
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Text color={isDark ? "#C3C9D3" : "gray.600"}>No deleted audits found.</Text>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                variant="gold"
                mr={3}
                onClick={() => setIsDeletedAuditModalOpen(false)}
              >
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal
          isOpen={isAuditModalOpen}
          onClose={() => setIsAuditModalOpen(false)}
          size="xl"
          closeOnOverlayClick={false}
          closeOnEsc={true}
          portalProps={
            isFullscreen ? { containerRef: fullscreenContainerRef } : undefined
          }
        >
          <ModalOverlay />
          <ModalContent maxWidth="800px" bg={isDark ? "#1A1C1F" : "white"} borderWidth="1px" borderColor={isDark ? "#2E2F33" : "gray.200"} className="rounded-xl shadow-2xl max-h-[80vh]">
            <ModalHeader color={isDark ? "white" : "#111827"} bgGradient={"linear(to-r, #FBD282, #B59143)"} className="text-sm font-semibold tracking-wide rounded-t-xl">
              Audit Details — {selectedTaskName}
            </ModalHeader>
            <ModalCloseButton color={isDark ? "white" : "#111827"} />
            <ModalBody color={isDark ? "white" : "#111827"} className="p-0">
              {selectedAudit.length > 0 ? (
                <Box className="max-h-[60vh] overflow-y-auto" bg={isDark ? "#14161B" : "white"}>
                  <Table variant="simple" className="text-[11px] md:text-xs">
                    <Thead position="sticky" top={0} zIndex={3} bg={isDark ? "#14161B" : "white"} className="sticky top-0 z-10">
                      <Tr>
                        <Th className="text-[10px] md:text-xs text-gray-600 font-semibold tracking-wide whitespace-nowrap">
                          Field
                        </Th>
                        <Th className="text-[10px] md:text-xs text-gray-600 font-semibold tracking-wide whitespace-nowrap">
                          Previous Value
                        </Th>
                        <Th className="text-[10px] md:text-xs text-gray-600 font-semibold tracking-wide whitespace-nowrap">
                          New Value
                        </Th>
                        <Th className="text-[10px] md:text-xs text-gray-600 font-semibold tracking-wide whitespace-nowrap">
                          Updated At
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {selectedAudit
                        .sort(
                          (a, b) =>
                            new Date(b.updatedAt).getTime() - // Sort by date (descending)
                            new Date(a.updatedAt).getTime()
                        )
                        .map((audit, index) => (
                          <Tr
                            key={index}
                            className="transition-colors"
                            style={{ backgroundColor: isDark ? (index % 2 === 0 ? "#1A1C1F" : "#14161B") : (index % 2 === 0 ? "#FFFFFF" : "#F9FAFB"), color: isDark ? "white" : "#111827" }}
                          >
                            <Td className=" font-medium whitespace-nowrap">
                              {audit.field}
                            </Td>
                            <Td className="">
                              {formatAuditValue(
                                audit.field,
                                audit.previousValue
                              )}
                            </Td>
                            <Td className="">
                              {formatAuditValue(audit.field, audit.newValue)}
                            </Td>
                            <Td className=" whitespace-nowrap">
                              {new Date(audit.updatedAt).toLocaleString()}
                            </Td>
                          </Tr>
                        ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text color={isDark ? "#C3C9D3" : "#6B7280"} className="text-xs py-4 text-center">
                  No audit logs found for this task.
                </Text>
              )}
            </ModalBody>
            <ModalFooter borderTopWidth="1px" borderTopColor={isDark ? "#2E2F33" : "gray.200"}>
              <Button variant="gold" size="sm" mr={3} onClick={() => setIsAuditModalOpen(false)}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          closeOnOverlayClick={false}
          closeOnEsc={true}
          portalProps={
            isFullscreen ? { containerRef: fullscreenContainerRef } : undefined
          }
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader color={color}>Delete Task</ModalHeader>
            <ModalCloseButton color={color} />
            <ModalBody color={color}>
              <Text mb={4}>
                Are you sure you want to delete the task? This action cannot be
                undone.
              </Text>
              <Text fontWeight="bold" mb={2}>
                Task Name:{" "}
                {tasks.find((task) => task.id === editingTaskId)?.name || "N/A"}
              </Text>
              <FormControl>
                <FormLabel>Reason for Deletion : </FormLabel>
                <Textarea
                  placeholder="Please provide a reason for deleting this task..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={handleCloseDeleteModal}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={() => {
                  handleDeleteTask(deleteReason);
                  handleCloseDeleteModal();
                }}
                isDisabled={!deleteReason.trim()} // Disable if no reason is provided
              >
                Confirm Delete
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          size="lg"
          closeOnOverlayClick={false}
          closeOnEsc={true}
          portalProps={
            isFullscreen ? { containerRef: fullscreenContainerRef } : undefined
          }
        >
          <ModalOverlay />
          <ModalContent
            maxHeight="83vh"
            overflow="auto"
            minWidth={550}
            borderRadius="md"
            boxShadow="xl"
          >
            <ModalHeader
              fontSize="md"
              fontWeight="semibold"
              color={modalHeaderColor}
              fontFamily="'Inter', sans-serif"
            >
              Add New Task
            </ModalHeader>
            <ModalCloseButton color={color} />
            <ModalBody
              color={color}
              mt={"-15px"}
              pb={3}
              fontFamily="'Inter', sans-serif"
            >
              <FormControl id="taskName" isRequired>
                <FormLabel fontSize="xs" fontWeight="bold">
                  Task Name
                </FormLabel>
                <Input
                  value={newTask.name}
                  onChange={handleInputChange}
                  name="name"
                  placeholder="Enter task name"
                  fontSize="xs"
                  px={3}
                  h="25px"
                  required
                  borderColor="gray.300"
                  color={inputTextColor}
                  _hover={{ borderColor: "yellow.500" }}
                  _focus={{ borderColor: "yellow.500" }}
                  isReadOnly={!canEditCurrentTask}
                  onClick={() => !canEditCurrentTask && showPermissionToast()}
                />
              </FormControl>

              <FormControl mt={2} id="planned" isRequired>
                <FormLabel fontSize="xs" fontWeight="bold">
                  Planned
                </FormLabel>
                <Flex justify="space-between" align="center" mt={1}>
                  <Box flex="1" mr={2}>
                    <FormLabel textAlign="center" fontSize="xs">
                      Start
                    </FormLabel>
                    <Input
                      type="date"
                      value={newTask.plannedStart}
                      onChange={handleInputChange}
                      name="plannedStart"
                      fontSize="xs"
                      px={3}
                      h="25px"
                      borderColor="gray.300"
                      color={inputTextColor}
                      _hover={{ borderColor: "yellow.500" }}
                      _focus={{ borderColor: "yellow.500" }}
                      isReadOnly={!canEditCurrentTask}
                      onClick={() => !canEditCurrentTask && showPermissionToast()}
                    />
                  </Box>

                  <Box flex="1" mx={2}>
                    <FormLabel textAlign="center" fontSize="xs">
                      Duration (in days)
                    </FormLabel>
                    <Flex alignItems="center" justify="center">
                      <Button
                        h="25px"
                        onClick={() => {
                          if (!canEditCurrentTask) {
                            showPermissionToast();
                            return;
                          }
                          handleDurationChange(-1);
                        }}
                        fontSize="sm"
                        variant="outline"
                        colorScheme="yellow"
                        _hover={{ bg: "yellow.100" }}
                      >
                        -
                      </Button>
                      <Input
                        type="number"
                        value={
                          newTask.duration === 0 && !isFocused
                            ? "0"
                            : newTask.duration || ""
                        }
                        onChange={handleDurationInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        mx={3}
                        maxLength={6}
                        width="60px"
                        h="25px"
                        textAlign="center"
                        fontSize="xs"
                        px={2}
                        borderColor="gray.300"
                        color={inputTextColor}
                        _hover={{ borderColor: "yellow.500" }}
                        _focus={{ borderColor: "yellow.500" }}
                        isReadOnly={!canEditCurrentTask}
                        onClick={() => !canEditCurrentTask && showPermissionToast()}
                      />
                      <Button
                        h="25px"
                        onClick={() => {
                          if (!canEditCurrentTask) {
                            showPermissionToast();
                            return;
                          }
                          handleDurationChange(1);
                        }}
                        fontSize="sm"
                        variant="outline"
                        colorScheme="yellow"
                        _hover={{ bg: "yellow.100" }}
                      >
                        +
                      </Button>
                    </Flex>
                  </Box>

                  <Box flex="1" ml={2}>
                    <FormLabel textAlign="center" fontSize="xs">
                      End
                    </FormLabel>
                    <Input
                      type="date"
                      value={newTask.plannedEnd}
                      isReadOnly
                      disabled
                      fontSize="xs"
                      px={3}
                      h="25px"
                      borderColor="gray.300"
                      color={inputTextColor}
                      _focus={{ borderColor: "gray.300" }}
                    />
                  </Box>
                </Flex>
              </FormControl>

              <FormControl mt={2} id="actual" isRequired>
                <FormLabel fontSize="xs" fontWeight="bold">
                  Actual
                </FormLabel>
                <Flex justify="space-between" align="center" mt={1}>
                  <Box flex="1" mr={2}>
                    <FormLabel textAlign="center" fontSize="xs">
                      Start
                    </FormLabel>
                    <Input
                      type="date"
                      value={newTask.actualStart}
                      onChange={handleInputChange}
                      name="actualStart"
                      fontSize="xs"
                      px={3}
                      h="25px"
                      borderColor="gray.300"
                      color={inputTextColor}
                      _hover={{ borderColor: "yellow.500" }}
                      _focus={{ borderColor: "yellow.500" }}
                      isReadOnly={!canEditCurrentTask}
                      onClick={() => !canEditCurrentTask && showPermissionToast()}
                    />
                  </Box>

                  <Box flex="1" mx={2}>
                    <FormLabel textAlign="center" fontSize="xs">
                      Duration (in days)
                    </FormLabel>
                    <Flex alignItems="center" justify="center">
                      <Button
                        h="25px"
                        onClick={() => {
                          if (!canEditCurrentTask) {
                            showPermissionToast();
                            return;
                          }
                          handleActualDurationChange(-1);
                        }}
                        fontSize="xs"
                        variant="outline"
                        colorScheme="yellow"
                        _hover={{ bg: "yellow.100" }}
                      >
                        -
                      </Button>
                      <Input
                        type="number"
                        h="25px"
                        value={
                          newTask.actualDuration === 0 && !isFocused
                            ? "0"
                            : newTask.actualDuration || ""
                        }
                        onChange={handleActualDurationInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        mx={3}
                        maxLength={6}
                        width="60px"
                        textAlign="center"
                        fontSize="xs"
                        px={2}
                        borderColor="gray.300"
                        color={inputTextColor}
                        _hover={{ borderColor: "yellow.500" }}
                        _focus={{ borderColor: "yellow.500" }}
                        isReadOnly={!canEditCurrentTask}
                        onClick={() => !canEditCurrentTask && showPermissionToast()}
                      />
                      <Button
                        h="25px"
                        onClick={() => {
                          if (!canEditCurrentTask) {
                            showPermissionToast();
                            return;
                          }
                          handleActualDurationChange(1);
                        }}
                        fontSize="sm"
                        variant="outline"
                        colorScheme="yellow"
                        _hover={{ bg: "yellow.100" }}
                      >
                        +
                      </Button>
                    </Flex>
                  </Box>

                  <Box flex="1" ml={2}>
                    <FormLabel textAlign="center" fontSize="xs">
                      End
                    </FormLabel>
                    <Input
                      h="25px"
                      type="date"
                      value={newTask.actualEnd}
                      isReadOnly
                      disabled
                      fontSize="xs"
                      px={3}
                      borderColor="gray.300"
                      color={inputTextColor}
                      _focus={{ borderColor: "gray.300" }}
                    />
                  </Box>
                </Flex>
              </FormControl>

              {/* <FormControl mt={2} id="dependency" fontWeight="bold" isRequired>
                <FormLabel fontSize="xs" fontWeight="bold">
                  Dependency
                </FormLabel>
                <Input
                  h="25px"
                  value={newTask.dependency}
                  onChange={handleInputChange}
                  name="dependency"
                  placeholder="Dependency task"
                  fontSize="xs"
                  px={3}
                  borderColor="gray.300"
                  _hover={{ borderColor: "purple.500" }}
                  _focus={{ borderColor: "purple.500" }}
                />
              </FormControl> */}

              <FormControl mt={2} id="role" isRequired>
                <FormLabel fontSize="xs" fontWeight="bold" mt={2}>
                  Assign Users
                </FormLabel>
                <Select
                  h="25px"
                  placeholder="Select users"
                  isDisabled={!canEditCurrentTask}
                  onChange={(e) => {
                    if (!canEditCurrentTask) {
                      showPermissionToast();
                      return;
                    }
                    handleUserSelect(e);
                  }}
                  fontSize="xs"
                  px={1}
                  borderColor="gray.300"
                  color={inputTextColor}
                  _hover={{ borderColor: "yellow.500" }}
                  _focus={{ borderColor: "yellow.500" }}
                  isReadOnly={!canEditCurrentTask}
                >
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} - {user.email}
                    </option>
                  ))}
                </Select>

                <Box mt={2}>
                  {(newTask.assignedUsers || [])
                    .filter((id) => users.some((u) => u.id === id))
                    .map((userId) => {
                      const user = users.find((u) => u.id === userId);
                      return (
                        <Tag
                          size="sm"
                          key={userId}
                          borderRadius="full"
                          variant="outline"
                          colorScheme="yellow"
                          borderColor="yellow.500"
                          color={goldTextColor}
                          bg={goldBgColor}
                          m={1}
                        >
                          <HStack spacing={3}>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold" fontSize="xs" color={goldTextColor}>
                                {user?.firstName + " - " + user?.email}
                              </Text>
                            </VStack>
                            <TagCloseButton
                              isDisabled={!canEditCurrentTask}
                              cursor={!canEditCurrentTask ? "not-allowed" : "pointer"}
                              onClick={() => {
                                if (!canEditCurrentTask) {
                                  showPermissionToast();
                                  return;
                                }
                                handleRemoveUser(userId);
                              }}
                            />
                          </HStack>
                        </Tag>
                      );
                    })}
                </Box>
              </FormControl>
              <Flex justify="space-between">
                <FormControl mt={2} id="risk" isRequired width="48%">
                  <FormLabel fontSize="xs" fontWeight="bold">
                    Type
                  </FormLabel>
                  <Select
                    h="25px"
                    value={newTask.type}
                    onChange={(e) => {
                      if (!canEditCurrentTask) {
                        showPermissionToast();
                        return;
                      }
                      handleTypeChange(e);
                    }}
                    name="risk"
                    fontSize="xs"
                    px={1}
                    borderColor="gray.300"
                    color={inputTextColor}
                    _hover={{ borderColor: "yellow.500" }}
                    _focus={{ borderColor: "yellow.500" }}
                    isReadOnly={!canEditCurrentTask}
                  >
                    <option value="task">task</option>
                  </Select>
                </FormControl>
                {/* <FormControl mt={2} id="stage" isRequired width="48%">
                  <FormLabel fontSize="xs" fontWeight="bold">
                    Stage
                  </FormLabel>
                  <Select
                    h="25px"
                    value={newTask.stage}
                    onChange={handleStageChange}
                    name="stage"
                    fontSize="xs"
                    px={1}
                    borderColor="gray.300"
                    _hover={{ borderColor: "teal.500" }}
                    _focus={{ borderColor: "teal.500" }}
                  >
                    <option value="DR-1">DR-1</option>
                    <option value="DR-2">DR-2</option>
                    <option value="DR`">DR-3</option>
                  </Select>
                </FormControl> */}
              </Flex>
              <Flex justify="space-between">
                <FormControl mt={2} id="status" isRequired width="48%">
                  <FormLabel fontSize="xs" fontWeight="bold">
                    Status
                  </FormLabel>
                  <Select
                    value={newTask.status}
                    onChange={handleStausChange}
                    name="status"
                    fontSize="xs"
                    h="25px"
                    px={1}
                    borderColor="gray.300"
                    color={inputTextColor}
                    _hover={{ borderColor: "yellow.500" }}
                    _focus={{ borderColor: "yellow.500" }}
                  >
                    <option value="Not-started">Not Started</option>
                    <option value="In-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="delay">Delay</option>
                  </Select>
                </FormControl>

                <FormControl mt={2} id="risk" isRequired width="48%">
                  <FormLabel fontSize="xs" fontWeight="bold">
                    Risk Level
                  </FormLabel>
                  <Select
                    h="25px"
                    value={newTask.risk}
                    onChange={(e) => {
                      if (!canEditCurrentTask) {
                        showPermissionToast();
                        return;
                      }
                      handleRiskChange(e);
                    }}
                    name="risk"
                    fontSize="xs"
                    px={1}
                    borderColor="gray.300"
                    color={inputTextColor}
                    _hover={{ borderColor: "yellow.500" }}
                    _focus={{ borderColor: "yellow.500" }}
                    isReadOnly={!canEditCurrentTask}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </Select>
                </FormControl>
              </Flex>
              <FormControl mt={2} id="progress">
                <FormLabel fontSize="xs" fontWeight="bold">
                  Progress
                </FormLabel>
                <Box>
                  <Box position="relative" mt={"-3px"} width="100%" mb={2}>
                    <Slider
                      value={newTask.progress}
                      onChange={handleProgressChange}
                      focusThumbOnChange={false}
                      min={0}
                      max={100}
                      step={1}
                      aria-label="Progress slider"
                      size="sm"
                      onMouseDown={handleDragStart}
                      onMouseUp={handleDragEnd}
                      isDisabled={newTask.status === "Not-started"}
                    >
                      <SliderTrack bg={isDark ? "gray.700" : "gray.100"}>
                        <SliderFilledTrack bg="yellow.400" />
                      </SliderTrack>
                      <SliderThumb boxSize={4} border="2px solid" borderColor="yellow.400" />
                    </Slider>
                    <Box
                      ref={tooltipRef}
                      position="absolute"
                      top={1}
                      left={90}
                      transform="translateX(-50%) translateY(-100%)"
                      bg="gray.700"
                      color="white"
                      height={7}
                      width={7}
                      borderRadius="50%"
                      padding="4px"
                      py={2}
                      textAlign="center"
                      fontSize="9px"
                      opacity={newTask.progress === 0 ? 0 : 1}
                    >
                      {Math.round(newTask.progress)}%
                    </Box>
                  </Box>
                  <Input
                    type="number"
                    value={
                      newTask.progress === 0 && !isProgressFocused
                        ? "0"
                        : Math.round(newTask.progress).toString()
                    }
                    onChange={(e) => {
                      const strVal = e.target.value;
                      if (strVal === "") {
                        handleProgressChange(0);
                        return;
                      }
                      let val = parseInt(strVal);
                      if (isNaN(val)) val = 0;
                      if (val < 0) val = 0;
                      if (val > 100) val = 100;
                      handleProgressChange(val);
                    }}
                    onFocus={() => setIsProgressFocused(true)}
                    onBlur={() => setIsProgressFocused(false)}
                    min={0}
                    max={100}
                    width="100%"
                    h="30px"
                    fontSize="xs"
                    textAlign="left"
                    px={2}
                    placeholder="Enter progress percentage"
                    borderColor="gray.300"
                    color={inputTextColor}
                    _hover={{ borderColor: "yellow.500" }}
                    _focus={{ borderColor: "yellow.500" }}
                    isDisabled={newTask.status === "Not-started"}
                  />
                </Box>
              </FormControl>
            </ModalBody>

            <ModalFooter mt={"-15px"} justifyContent="space-between">
              <Flex gap={2}>
                {canEditCurrentTask && (
                  <Button
                    height={"30px"}
                    variant="gold"
                    onClick={editingTaskId ? handleUpdateTask : handleSaveTask}
                    fontSize="sm"
                    isLoading={isSavingOrUpdating} // Add isLoading prop
                    loadingText={editingTaskId ? "Updating..." : "Saving..."} // Optional: show text while loading
                    spinnerPlacement="start"
                    isDisabled={isSavingOrUpdating} // Disable button while loading
                  >
                    {editingTaskId ? "Update Task" : "Save Task"}
                  </Button>
                )}
                <Button
                  height={"30px"}
                  variant="ghost"
                  onClick={handleCloseModal}
                  fontSize="sm"
                  color={inputTextColor}
                >
                  Cancel
                </Button>
              </Flex>
              {editingTaskId && canDelete(newTask || undefined) && (
                <Button
                  height={"30px"}
                  colorScheme="red"
                  onClick={handleOpenDeleteModal}
                  fontSize="sm"
                >
                  Delete Task
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Import Review Modal */}
        <Modal isOpen={isReviewModalOpen} onClose={() => setIsReviewModalOpen(false)} size="4xl" scrollBehavior="inside">
          <ModalOverlay backdropFilter="blur(4px)" />
          <ModalContent bg={isDark ? "#1A1C1F" : "white"} borderRadius="xl">
            <ModalHeader borderBottomWidth="1px" borderColor={isDark ? "#2E2F33" : "gray.100"}>
              <HStack justify="space-between" pr={6}>
                <Text fontSize="lg" fontWeight="bold" color={isDark ? "white" : "gray.800"}>
                  Review Import Changes
                </Text>
                <Badge colorScheme="blue" borderRadius="full" px={3}>
                  {pendingChanges.updates.length} Updates | {pendingChanges.newTasks.length} New
                </Badge>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p={0}>
              <Box p={6}>
                {pendingChanges.updates.length > 0 && (
                  <>
                    <Text fontWeight="bold" mb={4} color="blue.500">Updates to Existing Tasks:</Text>
                    <Table variant="simple" size="sm" mb={8} border="1px" borderColor={isDark ? "#2E2F33" : "gray.100"}>
                      <Thead bg={isDark ? "#14161B" : "gray.50"}>
                        <Tr>
                          <Th w="100px">WBS</Th>
                          <Th w="200px">Task Name</Th>
                          <Th>Field</Th>
                          <Th>Current Value</Th>
                          <Th>New Value</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {pendingChanges.updates.map((update, idx) => (
                          <React.Fragment key={idx}>
                            {update.changes.map((change, cIdx) => (
                              <Tr key={`${idx}-${cIdx}`}>
                                {cIdx === 0 && (
                                  <>
                                    <Td rowSpan={update.changes.length} fontWeight="bold" verticalAlign="top">{update.wbs}</Td>
                                    <Td rowSpan={update.changes.length} verticalAlign="top" isTruncated maxW="200px">{update.name}</Td>
                                  </>
                                )}
                                <Td color="gray.500">{change.field}</Td>
                                <Td color="red.400" textDecoration="line-through">{change.old || "N/A"}</Td>
                                <Td color="green.400" fontWeight="bold">{change.new || "N/A"}</Td>
                              </Tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </Tbody>
                    </Table>
                  </>
                )}

                {pendingChanges.newTasks.length > 0 && (
                  <>
                    <Text fontWeight="bold" mb={4} color="green.500">New Tasks to be Added:</Text>
                    <Box maxHeight="400px" overflowY="auto" borderRadius="md" border="1px" borderColor={isDark ? "#2E2F33" : "gray.100"}>
                      <Table variant="simple" size="sm">
                        <Thead bg={isDark ? "#14161B" : "gray.50"} position="sticky" top={0} zIndex={1}>
                          <Tr>
                            <Th>WBS</Th>
                            <Th>Name</Th>
                            <Th>Planned Start</Th>
                            <Th>Planned End</Th>
                            <Th>Duration</Th>
                            <Th>Status</Th>
                            <Th>Assigned Users</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {pendingChanges.newTasks.map((t: any, idx) => (
                            <Tr key={idx}>
                              <Td fontWeight="bold">{t.wbs}</Td>
                              <Td isTruncated maxW="150px">{t.name}</Td>
                              <Td fontSize="xs">{formatDateForDisplay(t.plannedStart)}</Td>
                              <Td fontSize="xs">{formatDateForDisplay(t.plannedEnd)}</Td>
                              <Td>{t.duration}d</Td>
                              <Td><Badge size="xs" colorScheme={t.status === "Completed" ? "green" : "blue"}>{t.status}</Badge></Td>
                              <Td>
                                <Tooltip label={t._displayEmails || "None"} placement="top" hasArrow>
                                  <Text fontSize="xs" color="gray.500" isTruncated maxW="200px" cursor="help">
                                    {t._displayEmails || "None"}
                                  </Text>
                                </Tooltip>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </>
                )}

                {pendingChanges.updates.length === 0 && pendingChanges.newTasks.length === 0 && (
                  <Text textAlign="center" py={10} color="gray.500">No changes detected. The file data matches your current tasks.</Text>
                )}
              </Box>
            </ModalBody>
            <ModalFooter borderTopWidth="1px" borderColor={isDark ? "#2E2F33" : "gray.100"}>
              <Button variant="ghost" mr={3} onClick={() => setIsReviewModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="gold" 
                onClick={handleConfirmImport}
                isLoading={isSavingOrUpdating}
                isDisabled={pendingChanges.updates.length === 0 && pendingChanges.newTasks.length === 0}
              >
                Apply Changes
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>

    </>
    );
  }
);

const TimelinePanel: React.FC<{
  width: string;
  organizationId: string;
  selectedTaskId: string;
  zoomLevel: string;
  expandedTaskId: string | null;
  taskId: string | null;
  projectId: string;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  handleSync: () => void;
  channelId: string;
  tasks: Task[];
  setTask: React.Dispatch<React.SetStateAction<Task[]>>;
  onZoomChange: (level: string) => void;
}> = ({
  zoomLevel,
  onZoomChange,
  width,
  selectedTaskId,
  expandedTaskId,
  taskId,
  handleSync,
  organizationId,
  projectId,
  channelId,
  isEditMode,
  setIsEditMode,
  tasks,
  setTask,
}) => {
    const [connections, setConnections] = useState<
      {
        fromId: string;
        toId: string;
        startX: number;
        startY: number;
        endX: number;
        endY: number;
      }[]
    >([]);

    const [nodes, setNodes] = useNodesState([]);
    const [edges, setEdges] = useEdgesState([]);

    const generateEdges = () => {
      const newEdges = connections.map((connection) => ({
        id: `${connection.fromId}-${connection.toId}`,
        source: connection.fromId,
        target: connection.toId,
        animated: true,
        style: { stroke: "orange", strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.Arrow, // Correct markerEnd usage
        },
      }));
      setEdges(newEdges);
    };

    useEffect(() => {
      generateEdges();
    }, [connections]);

    const onConnect = (params: Connection) => {
      const edge: Edge = {
        id: `${params.source}-${params.target}`,
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle || "default",
        targetHandle: params.targetHandle || "default",
        type: "default",
        style: { stroke: "orange" },
      };

      setEdges((prevEdges) => [...prevEdges, edge]);
    };

    const [selectedConnection, setSelectedConnection] = useState<{
      fromId: string;
      toId: string;
      fromTaskName: string;
      toTaskName: string;
    } | null>(null);

    const { isOpen, onOpen, onClose } = useDisclosure();

    const getTaskNameById = (tasks: Task[], taskId: string): string | null => {
      for (const task of tasks) {
        if (task.id === taskId) {
          return task.name; // Return the name if the ID matches
        }
        if (task.subtasks && task.subtasks.length > 0) {
          const subtaskName = getTaskNameById(task.subtasks, taskId);
          if (subtaskName) {
            return subtaskName; // Return if found in subtasks
          }
        }
      }
      return null; // Return null if not found
    };

    const getRowBackground = (
      status: string | undefined,
      isSelected: boolean
    ): string => {
      if (isSelected) return "purple.50";
      const normalized = (status || "").toLowerCase();
      if (normalized === "completed") return "green.50";
      if (normalized === "delay") return "red.50";
      return "white";
    };

    const handleSaveConnection = () => {
      console.log("Connection saved:", selectedConnection);
      onClose(); // Close the modal after saving
    };

    const [draggingConnection, setDraggingConnection] = useState<{
      fromId: string;
      startX: number;
      startY: number;
      endX?: number; // Make it optional to handle initialization
      endY?: number; // Make it optional to handle initialization
      point: "start" | "end";
    } | null>(null);

    const [mousePosition, setMousePosition] = useState<{
      x: number;
      y: number;
    } | null>(null);

    console.log("Connections:", connections);

    const handleCircleDragStart = (
      e: React.MouseEvent<SVGCircleElement>,
      taskId: string,
      point: "start" | "end"
    ) => {
      const circle = e.currentTarget.getBoundingClientRect();
      const svg = timelineRef.current?.getBoundingClientRect();

      if (svg) {
        const startX = circle.x - svg.x + circle.width / 2; // Center X
        const startY = circle.y - svg.y + circle.height / 2; // Center Y

        setDraggingConnection({
          fromId: taskId,
          startX,
          startY,
          point,
        });
      }
    };

    const handleCircleDragEnd = (
      e: React.MouseEvent<SVGCircleElement>,
      toId: string
    ) => {
      if (!draggingConnection) return; // No active drag connection

      const circle = e.currentTarget.getBoundingClientRect(); // Get target circle
      const svg = timelineRef.current?.getBoundingClientRect();

      if (svg) {
        const endX = circle.x - svg.x + circle.width / 2; // Center X
        const endY = circle.y - svg.y + circle.height / 2; // Center Y

        // Check for duplicate connections
        setConnections((prev) => {
          const exists = prev.some(
            (conn) =>
              conn.fromId === draggingConnection.fromId && conn.toId === toId
          );
          if (exists) return prev;

          // Add the new connection
          return [
            ...prev,
            {
              fromId: draggingConnection.fromId,
              toId,
              startX: draggingConnection.startX,
              startY: draggingConnection.startY,
              endX,
              endY,
            },
          ];
        });
      }

      setDraggingConnection(null); // Reset the temporary connection
    };

    const handleMouseMove = (coords: { clientX: number; clientY: number }) => {
      if (draggingConnection) {
        const svg = timelineRef.current?.getBoundingClientRect();

        if (svg) {
          const endX = coords.clientX - svg.x;
          const endY = coords.clientY - svg.y;

          setDraggingConnection((prev) =>
            prev ? { ...prev, endX, endY } : null
          );
        }
      }
    };

    useEffect(() => {
      const handleMouseUp = () => setDraggingConnection(null);

      const nativeMouseMoveHandler = (e: MouseEvent) => {
        handleMouseMove({
          clientX: e.clientX,
          clientY: e.clientY,
        });
      };

      if (draggingConnection) {
        window.addEventListener("mousemove", nativeMouseMoveHandler);
        window.addEventListener("mouseup", handleMouseUp);
      }

      return () => {
        window.removeEventListener("mousemove", nativeMouseMoveHandler);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }, [draggingConnection]);

    const fetchConnectionsFromFirebase = async (taskId: string | null) => {
      try {
        const projectRef = doc(
          db,
          `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
        );
        const snapshot = await getDoc(projectRef);

        if (!snapshot.exists()) {
          console.warn(`No document found for taskId: ${taskId}`);
          return [];
        }

        const data = snapshot.data();
        console.log("Fetched data from Firebase:", data);

        // Ensure `links` exists and is an array
        if (!data.links || !Array.isArray(data.links)) {
          console.warn("Connections data is not in the expected format:", data);
          return [];
        }

        const connections = data.links.map(
          (connection: {
            id: any;
            fromId: any;
            toId: any;
            startX: any;
            startY: any;
            endX: any;
            endY: any;
          }) => ({
            id: connection.id,
            fromId: connection.fromId,
            toId: connection.toId,
            startX: connection.startX,
            startY: connection.startY,
            endX: connection.endX,
            endY: connection.endY,
          })
        );

        console.log("Fetched connections from Firebase:", connections);
        return connections;
      } catch (error) {
        console.error("Error fetching connections from Firebase:", error);
        showToast({
          title: "Error",
          description: "Failed to fetch connections from Firebase.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return [];
      }
    };

    const handleDeleteConnection = async (
      taskId: string | null // Default to static projectId
    ) => {
      if (!selectedConnection) {
        console.warn("No connection selected for deletion.");
        return;
      }

      try {
        const projectRef = doc(
          db,
          `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
        ); // Reference scoped to projectId
        const snapshot = await getDoc(projectRef);

        if (snapshot.exists()) {
          const data = snapshot.data();

          if (Array.isArray(data.links)) {
            const updatedConnections = data.links.filter(
              (connection) =>
                connection.fromId !== selectedConnection.fromId ||
                connection.toId !== selectedConnection.toId
            );

            await setDoc(
              projectRef,
              { links: updatedConnections },
              { merge: true }
            );

            setConnections(updatedConnections);
            setSelectedConnection(null);

            showToast({
              title: "Connection Deleted",
              description:
                "The selected connection has been successfully deleted.",
              status: "success",
              duration: 3000,
              isClosable: true,
            });
          } else {
            console.warn("Connections data is not in the expected format:", data);
          }
        } else {
          console.warn(`No document found for projectId: ${projectId}`);
        }
      } catch (error) {
        console.error("Error deleting connection:", error);
        showToast({
          title: "Error",
          description: "Failed to delete the selected connection.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    useEffect(() => {
      const loadConnections = async () => {
        const fetchedConnections = await fetchConnectionsFromFirebase(taskId);
        setConnections(fetchedConnections);
      };

      loadConnections();
    }, []);

    type ZoomLevel = "days" | "weeks" | "months" | "quarters" | "years";

    const zoomScaleFactors: Record<ZoomLevel, number> = {
      days: 1,
      weeks: 7,
      months: 30,
      quarters: 90,
      years: 365,
    };

    // Example scale factor function
    function getZoomScaleFactor(zoomLevel: string): number {
      switch (zoomLevel) {
        case "days":
          return 30; // e.g. 30px per day
        case "weeks":
          return 40; // e.g. 40px per week
        case "months":
          return 60; // e.g. 60px per month
        case "quarters":
          return 90; // e.g. 90px per quarter
        case "years":
          return 150; // e.g. 150px per year
        default:
          return 30; // Fallback for unknown zoom levels
      }
    }

    const renderTemporaryLine = () => {
      if (!draggingConnection || draggingConnection.endX === undefined)
        return null;

      return (
        <line
          x1={draggingConnection.startX}
          y1={draggingConnection.startY}
          x2={draggingConnection.endX}
          y2={draggingConnection.endY}
          stroke="gray"
          strokeWidth={2}
          strokeDasharray="4"
          markerEnd="url(#arrowhead)"
        />
      );
    };

    const { expandedTaskIds, toggleExpandedTask } = useTaskContext();

    const [temporaryConnectionLine, setTemporaryConnectionLine] = useState<{
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    } | null>(null);

    const [tooltipData, setTooltipData] = useState<{
      x: number;
      y: number;
      content: React.ReactNode;
    } | null>(null);

    const closeTooltip = () => {
      setTooltipData(null);
    };

    const timelineRef = useRef<HTMLDivElement>(null);
    const [timelineWidth, setTimelineWidth] = useState(0);

    const minDate = new Date(
      Math.min(...tasks.map((task) => new Date(task.plannedStart).getTime()))
    );
    const maxDate = new Date(
      Math.max(...tasks.map((task) => new Date(task.plannedEnd).getTime()))
    );

    const generateDateRange = (
      minDate: Date,
      maxDate: Date,
      zoomLevel: string
    ) => {
      const range = [];
      let currentDate = new Date(minDate);

      while (currentDate <= maxDate) {
        range.push(currentDate.toISOString().split("T")[0]);
        switch (zoomLevel) {
          case "days":
            currentDate.setDate(currentDate.getDate() + 1);
            break;
          case "weeks":
            currentDate.setDate(currentDate.getDate() + 7);
            break;
          case "months":
            currentDate.setMonth(currentDate.getMonth() + 1);
            break;
          case "quarters":
            currentDate.setMonth(currentDate.getMonth() + 3);
            break;
          case "years":
            currentDate.setFullYear(currentDate.getFullYear() + 1);
            break;
          default:
            currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      return range;
    };

    const dateRange = generateDateRange(minDate, maxDate, zoomLevel);

    useEffect(() => {
      setTimelineWidth(dateRange.length * 100); // Fixed width of 100px per cell
    }, [dateRange]);

    const calculateCirclePosition = (task: Task, point: "start" | "end") => {
      const barX = calculateBarPositionAndWidth(
        task.actualStart,
        task.actualEnd,
        minDate.toISOString(),
        maxDate.toISOString(),
        timelineWidth,
        dateRange
      ).position;

      const barWidth = calculateBarPositionAndWidth(
        task.actualStart,
        task.actualEnd,
        minDate.toISOString(),
        maxDate.toISOString(),
        timelineWidth,
        dateRange
      ).width;

      const barHeight = 14; // Bar height
      const circleRadius = 5;

      if (point === "start") {
        return {
          x: barX - circleRadius,
          y: barHeight / 2,
        };
      }

      if (point === "end") {
        return {
          x: barX + barWidth + circleRadius,
          y: barHeight / 2,
        };
      }

      return { x: 0, y: 0 }; // Fallback
    };

    const calculateBarPosition = (date: string, timelineWidth: number) => {
      const totalDuration =
        new Date(maxDate).getTime() - new Date(minDate).getTime();
      const elapsedTime = new Date(date).getTime() - new Date(minDate).getTime();
      return (elapsedTime / totalDuration) * timelineWidth;
    };

    const calculateBarCenter = (task: Task) => {
      const rowElement = document.querySelector(`[data-task-id="${task.id}"]`);
      if (rowElement) {
        const rowRect = rowElement.getBoundingClientRect();
        return rowRect.top + rowRect.height / 2; // Center of the row
      }
      return 0; // Fallback
    };

    const updateConnectionCoordinates = (
      taskId: string,
      newX: any,
      newY: any
    ) => {
      setConnections((prevConnections) =>
        prevConnections.map((connection) => {
          if (connection.fromId === taskId) {
            return {
              ...connection,
              startX: newX,
              startY: newY,
            };
          } else if (connection.toId === taskId) {
            return {
              ...connection,
              endX: newX,
              endY: newY,
            };
          }
          return connection;
        })
      );
    };

    const updateConnectionsOnDrag = (taskId: string) => {
      setConnections((prevConnections) =>
        prevConnections.map((connection) => {
          // Update `startX`, `startY` if the task is the "from" task
          if (connection.fromId === taskId) {
            const fromCircle = document.getElementById(`${taskId}-start`);
            if (fromCircle) {
              const rect = fromCircle.getBoundingClientRect();
              const svg = timelineRef.current?.getBoundingClientRect();
              if (svg) {
                return {
                  ...connection,
                  startX: rect.x - svg.x + rect.width / 2,
                  startY: rect.y - svg.y + rect.height / 2,
                };
              }
            }
          }

          // Update `endX`, `endY` if the task is the "to" task
          if (connection.toId === taskId) {
            const toCircle = document.getElementById(`${taskId}-end`);
            if (toCircle) {
              const rect = toCircle.getBoundingClientRect();
              const svg = timelineRef.current?.getBoundingClientRect();
              if (svg) {
                return {
                  ...connection,
                  endX: rect.x - svg.x + rect.width / 2,
                  endY: rect.y - svg.y + rect.height / 2,
                };
              }
            }
          }

          return connection; // Return unchanged connection if not related to this task
        })
      );
    };

    const handleDragStop = async (task: Task, dragX: number) => {
      const timelineDuration =
        new Date(maxDate).getTime() - new Date(minDate).getTime();
      const daysMoved = Math.round(
        (dragX / timelineWidth) * (timelineDuration / (1000 * 60 * 60 * 24))
      );

      // Helper function to update task dates recursively
      const updateTaskDates = (tasks: Task[], targetTaskId: string): Task[] =>
        tasks.map((t) => {
          if (t.id === targetTaskId) {
            return {
              ...t,
              actualStart: new Date(
                new Date(t.actualStart).getTime() + daysMoved * 86400000
              )
                .toISOString()
                .slice(0, 10),
              actualEnd: new Date(
                new Date(t.actualEnd).getTime() + daysMoved * 86400000
              )
                .toISOString()
                .slice(0, 10),
            };
          }

          // Recursively update subtasks
          if (t.subtasks && t.subtasks.length > 0) {
            return {
              ...t,
              subtasks: updateTaskDates(t.subtasks, targetTaskId),
            };
          }

          return t;
        });

      // Update tasks in the local state
      const updatedTasks = updateTaskDates(tasks, task.id);
      setTask(updatedTasks);

      try {
        // Firestore logic
        const projectRef = doc(
          db,
          `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
        );
        const projectSnapshot = await getDoc(projectRef);

        if (!projectSnapshot.exists()) {
          throw new Error("Project not found");
        }

        const currentData = projectSnapshot.data()?.data || [];

        // Helper to find the top-level parent task
        const findTopLevelTask = (tasks: Task[], id: string): Task | null => {
          for (const t of tasks) {
            if (t.id === id) return t;
            if (t.subtasks && t.subtasks.length > 0) {
              const found = findTopLevelTask(t.subtasks, id);
              if (found) return found;
            }
          }
          return null;
        };

        if (task.id.includes(".")) {
          // Task is a subtask
          const parentId = task.id.split(".").slice(0, -1).join(".");
          const topLevelParentId = parentId.split(".")[0]; // Get top-level parent ID
          const topLevelTask = findTopLevelTask(updatedTasks, topLevelParentId);

          if (topLevelTask) {
            // Update top-level task in Firestore
            await setDoc(projectRef, {
              ...projectSnapshot.data(),
              data: updatedTasks,
            });
            console.log("Updated top-level parent in Firestore:", topLevelTask);
          }
        } else {
          // Task is a top-level task
          const updatedTask = updatedTasks.find((t) => t.id === task.id);
          if (updatedTask) {
            // Update the task in Firestore
            await setDoc(projectRef, {
              ...projectSnapshot.data(),
              data: updatedTasks,
            });
            console.log("Task updated in Firestore:", updatedTask);
          }
        }
      } catch (error) {
        console.error("Error updating task in Firestore:", error);
      }

      // Update connections if required
      updateConnectionsOnDrag(task.id);
    };

    const calculateTaskXPosition = (task: Task, dragX: number): number => {
      const timelineDuration =
        new Date(maxDate).getTime() - new Date(minDate).getTime();
      const daysFromStart =
        (dragX / timelineWidth) * (timelineDuration / (1000 * 60 * 60 * 24));
      const newStart = new Date(minDate).getTime() + daysFromStart * 86400000;
      return (
        ((newStart - new Date(minDate).getTime()) / timelineDuration) *
        timelineWidth
      );
    };

    const calculateTaskYPosition = (task: Task): number => {
      const taskHeight = 60; // Height of each row
      const taskIndex = tasks.findIndex((t) => t.id === task.id);
      return taskIndex * taskHeight + taskHeight / 2; // Center within the row
    };

    const calculateBarWidth = (
      taskStart: string,
      taskEnd: string,
      cellStart: string,
      cellEnd: string,
      cellWidth: number
    ): string => {
      const taskStartTime = Math.max(
        new Date(taskStart).getTime(),
        new Date(cellStart).getTime()
      );
      const taskEndTime = Math.min(
        new Date(taskEnd).getTime(),
        new Date(cellEnd).getTime()
      );
      const taskDuration = Math.max(taskEndTime - taskStartTime, 0);
      const cellDuration =
        new Date(cellEnd).getTime() - new Date(cellStart).getTime();
      return `${(taskDuration / cellDuration) * cellWidth}px`;
    };

    const calculateBarPositionAndWidth = (
      taskStart: string,
      taskEnd: string,
      timelineStart: string,
      timelineEnd: string,
      totalTimelineWidth: number,
      dateRange: string[],
      isYearsZoomLevel: boolean = false
    ) => {
      const timelineStartTime = new Date(timelineStart).getTime();
      const timelineEndTime = new Date(timelineEnd).getTime();
      const taskStartTime = new Date(taskStart).getTime();
      const taskEndTime = new Date(taskEnd).getTime();

      const clampedStart = Math.max(taskStartTime, timelineStartTime);
      const clampedEnd = Math.min(taskEndTime, timelineEndTime);

      if (clampedStart >= timelineEndTime || clampedEnd <= timelineStartTime) {
        return { position: 0, width: 0 };
      }

      const timelineDuration = timelineEndTime - timelineStartTime;
      const rawPosition =
        ((clampedStart - timelineStartTime) / timelineDuration) *
        totalTimelineWidth;

      const rawWidth =
        ((clampedEnd - clampedStart) / timelineDuration) * totalTimelineWidth;

      const MIN_WIDTH = 90;
      const daysDiff = (clampedEnd - clampedStart) / (1000 * 60 * 60 * 24);

      let position = rawPosition;
      let width = rawWidth;

      // ✅ Only apply MIN_WIDTH for short tasks (1 day or less)
      if (rawWidth < MIN_WIDTH && daysDiff <= 1) {
        position = rawPosition - (MIN_WIDTH - rawWidth) / 2; // Center the bar
        width = MIN_WIDTH;
      }

      // 🔍 Special case for year zoom
      if (isYearsZoomLevel) {
        const yearDuration = 365 * 24 * 60 * 60 * 1000;
        const yearsCount = Math.ceil(timelineDuration / yearDuration);
        const totalWidth = yearsCount * 500;

        let yearPosition =
          ((clampedStart - timelineStartTime) / timelineDuration) * totalWidth;
        let yearWidth =
          ((clampedEnd - clampedStart) / timelineDuration) * totalWidth;

        if (yearWidth < MIN_WIDTH && daysDiff <= 1) {
          yearPosition = yearPosition - (MIN_WIDTH - yearWidth) / 2;
          yearWidth = MIN_WIDTH;
        }

        return { position: yearPosition, width: yearWidth };
      }

      return { position, width };
    };

    const calculateCellWidth = (
      cellStart: string,
      cellEnd: string,
      totalTimelineWidth: number,
      totalDuration: number
    ) => {
      const cellDuration =
        new Date(cellEnd).getTime() - new Date(cellStart).getTime();
      return (cellDuration / totalDuration) * totalTimelineWidth;
    };

    const renderDateRow = () => {
      const startYear = minDate.getFullYear();
      const endYear = maxDate.getFullYear();
      const startMonth = minDate.getMonth();
      const endMonth = maxDate.getMonth();
      const cellWidth = Math.max(timelineWidth / dateRange.length, 100);

      const totalDuration = maxDate.getTime() - minDate.getTime();
      const cellWidths = dateRange.map((_, idx) =>
        calculateCellWidth(
          dateRange[idx],
          dateRange[idx + 1] || maxDate.toISOString().split("T")[0],
          timelineWidth,
          totalDuration
        )
      );
      const ensureMinimumCells = (columns: React.ReactNode[]) => {
        while (columns.length < 14) {
          columns.push(
            <Th
              key={`placeholder-${columns.length}`}
              textAlign="center"
              borderColor="gray.200"
              borderRightWidth="1px"
              minWidth={`${cellWidth}px`}
            ></Th>
          );
        }
        return columns;
      };

      if (zoomLevel === "days") {
        const columns = dateRange.map((date, idx) => (
          <Th
            key={idx}
            textAlign="center"
            borderColor="gray.200"
            borderRightWidth="1px"
            minWidth={`${cellWidth}px`}
          >
            {new Date(date).toLocaleDateString("en-US", {
              day: "2-digit",
              month: "short",
            })}
          </Th>
        ));
        return <Tr>{ensureMinimumCells(columns)}</Tr>;
      } else if (zoomLevel === "weeks") {
        return (
          <Tr>
            {dateRange.map((date, idx) => (
              <Th
                key={idx}
                textAlign="center"
                borderColor="gray.200"
                borderRightWidth="1px"
                minWidth={`${cellWidth}px`}
              >
                {new Date(date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                })}
              </Th>
            ))}
          </Tr>
        );
      } else if (zoomLevel === "months") {
        return (
          <>
            <Tr>
              {Array.from(
                { length: endYear - startYear + 1 },
                (_, i) => startYear + i
              ).map((year) => (
                <Th
                  key={year}
                  textAlign="center"
                  colSpan={
                    year === startYear
                      ? 12 - startMonth
                      : year === endYear
                        ? endMonth + 1
                        : 12
                  }
                  borderColor="gray.200"
                  borderRightWidth="1px"
                  minWidth={`${cellWidth}px`}
                >
                  {year}
                </Th>
              ))}
            </Tr>
            <Tr>
              {Array.from(
                { length: endYear - startYear + 1 },
                (_, i) => startYear + i
              ).map((year, yearIdx) =>
                Array.from(
                  {
                    length:
                      year === startYear
                        ? 12 - startMonth
                        : year === endYear
                          ? endMonth + 1
                          : 12,
                  },
                  (_, monthIndex) => (
                    <Th
                      key={`${year}-${monthIndex}`}
                      textAlign="center"
                      borderColor="gray.200"
                      borderRightWidth="1px"
                      minWidth={`${cellWidth}px`}
                    >
                      {new Date(
                        year,
                        (yearIdx === 0 ? startMonth : 0) + monthIndex
                      ).toLocaleDateString("en-US", {
                        month: "short",
                      })}
                    </Th>
                  )
                )
              )}
            </Tr>
          </>
        );
      } else if (zoomLevel === "quarters") {
        return (
          <>
            <Tr>
              {Array.from(
                { length: endYear - startYear + 1 },
                (_, i) => startYear + i
              ).map((year) => (
                <Th
                  key={year}
                  textAlign="center"
                  colSpan={
                    year === startYear
                      ? 4 - Math.floor(startMonth / 3)
                      : year === endYear
                        ? Math.ceil((endMonth + 1) / 3)
                        : 4
                  }
                  borderColor="gray.200"
                  borderRightWidth="1px"
                  minWidth={`${cellWidth}px`}
                >
                  {year}
                </Th>
              ))}
            </Tr>
            <Tr>
              {Array.from(
                { length: endYear - startYear + 1 },
                (_, i) => startYear + i
              ).map((year) =>
                Array.from(
                  {
                    length:
                      year === startYear
                        ? 4 - Math.floor(startMonth / 3)
                        : year === endYear
                          ? Math.ceil((endMonth + 1) / 3)
                          : 4,
                  },
                  (_, quarterIndex) => (
                    <Th
                      key={`${year}-${quarterIndex}`}
                      textAlign="center"
                      borderColor="gray.200"
                      borderRightWidth="1px"
                      fontSize={"10px"}
                      minWidth={`${cellWidth}px`}
                    >
                      {
                        ["Jan-mar", "Apr-Jun", "Jul-Sep", "Oct-Dec"][
                        (year === startYear ? Math.floor(startMonth / 3) : 0) +
                        quarterIndex
                        ]
                      }
                    </Th>
                  )
                )
              )}
            </Tr>
          </>
        );
      } else if (zoomLevel === "years") {
        return (
          <>
            <Tr>
              <Th
                textAlign="center"
                colSpan={endYear - startYear + 1}
                borderColor="gray.200"
                borderRightWidth="1px"
                minWidth="500px"
              >
                {`${startYear} - ${endYear}`}
              </Th>
            </Tr>
            <Tr>
              {Array.from(
                { length: endYear - startYear + 1 },
                (_, i) => startYear + i
              ).map((year) => (
                <Th
                  key={year}
                  textAlign="center"
                  borderColor="gray.200"
                  borderRightWidth="1px"
                  minWidth="500px"
                >
                  {year}
                </Th>
              ))}
            </Tr>
          </>
        );
      }

      return null;
    };

    const DraggableOrStatic: React.FC<{
      isEditMode: boolean;
      children: React.ReactNode;
      onStop?: (e: DraggableEvent, data: DraggableData) => void;
    }> = ({ isEditMode, children, onStop }) => {
      if (isEditMode) {
        return (
          <Draggable axis="x" onStop={onStop}>
            <div style={{ position: "relative", overflow: "visible" }}>
              {children}
            </div>
          </Draggable>
        );
      }
      return <>{children}</>;
    };

    useEffect(() => {
      if (selectedConnection) {
        console.log("Selected connection:", selectedConnection);
      } else {
        console.log("No connection selected.");
      }
    }, [selectedConnection]);

    const updateConnections = (
      taskId: string,
      type: "start" | "end",
      x: number,
      y: number
    ) => {
      setConnections((prevConnections) =>
        prevConnections.map((connection) => {
          if (type === "start" && connection.fromId === taskId) {
            return { ...connection, startX: x, startY: y };
          }
          if (type === "end" && connection.toId === taskId) {
            return { ...connection, endX: x, endY: y };
          }
          return connection;
        })
      );
    };

    const { colorMode } = useColorMode();
    const isDark = colorMode === "dark";

    return (
      <Box
        ref={timelineRef}
        width={width}
        pt={1.5}
        overflowY="auto"
        height="90vh"
        position="relative"
      >
        {tooltipData && (
          <Box
            position="absolute"
            top={`${tooltipData.y}px`}
            left={`${tooltipData.x}px`}
            transform="translate(-50%, -100%)"
            bg="white"
            p={3}
            borderRadius="md"
            boxShadow="lg"
            zIndex={10}
            onClick={closeTooltip}
          >
            {tooltipData.content}
          </Box>
        )}

        <svg
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1, // Lower than the buttons
            width: "100%",
            height: "100%",
          }}
          onMouseMove={handleMouseMove}
        >
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="0"
              refY="3.5"
              orient="auto"
              pointerEvents="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="orange" />
            </marker>
          </defs>
          <div style={{ height: "500px", width: "100%" }}>
            <ReactFlow nodes={nodes} edges={edges} onConnect={onConnect} fitView>
              <Background />
              <Controls />
            </ReactFlow>
          </div>
          {renderTemporaryLine()}
        </svg>

        {/* Modal for showing connection details */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Connection Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedConnection && (
                <>
                  <Text mb={4}>
                    <strong>From:</strong> {selectedConnection.fromTaskName}
                    <br />
                    <strong>To:</strong> {selectedConnection.toTaskName}
                  </Text>
                  {/* <Text mb={4}>
              <strong>Lead/Lag:</strong> 0
            </Text> */}
                  <Flex justify="space-between">
                    <Box display="flex" gap={4}>
                      <Button colorScheme="blue" onClick={handleSaveConnection}>
                        Save
                      </Button>
                      <Button
                        colorScheme="red"
                        onClick={() => handleDeleteConnection(taskId)}
                      >
                        Delete
                      </Button>
                    </Box>
                    <Button variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                  </Flex>
                </>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>




        <Table border="1px" borderColor="gray.200" variant="simple">
          <Thead position={"sticky"} top={"0px"} bg={"white"} zIndex={2}>
            {renderDateRow()}
          </Thead>
          <Tbody border={"1px"}>
            {tasks.map((task) => {
              const { position: plannedPosition, width: plannedWidth } =
                zoomLevel === "years"
                  ? calculateBarPositionAndWidth(
                    task.plannedStart,
                    task.plannedEnd,
                    minDate.toISOString(),
                    maxDate.toISOString(),
                    timelineWidth,
                    dateRange,
                    true
                  )
                  : calculateBarPositionAndWidth(
                    task.plannedStart,
                    task.plannedEnd,
                    minDate.toISOString(),
                    maxDate.toISOString(),
                    timelineWidth,
                    dateRange
                  );

              const { position: actualPosition, width: actualWidth } =
                zoomLevel === "years"
                  ? calculateBarPositionAndWidth(
                    task.actualStart,
                    task.actualEnd,
                    minDate.toISOString(),
                    maxDate.toISOString(),
                    timelineWidth,
                    dateRange,
                    true
                  )
                  : calculateBarPositionAndWidth(
                    task.actualStart,
                    task.actualEnd,
                    minDate.toISOString(),
                    maxDate.toISOString(),
                    timelineWidth,
                    dateRange
                  );

              const calculateDateDifference = (
                date1: string | Date,
                date2: string | Date
              ): number => {
                const d1 = new Date(date1);
                const d2 = new Date(date2);
                const timeDifference = d1.getTime() - d2.getTime(); // Use getTime() for time in ms
                return Math.ceil(timeDifference / (1000 * 60 * 60 * 24)); // Difference in days
              };

              const renderSubtasks = (subtasks: Task[], level = 1) => {
                return subtasks.map((subtask) => {
                  const { position: subPlannedPosition, width: subPlannedWidth } =
                    calculateBarPositionAndWidth(
                      subtask.plannedStart,
                      subtask.plannedEnd,
                      minDate.toISOString(),
                      maxDate.toISOString(),
                      dateRange.length * 100,
                      dateRange
                    );

                  const { position: subActualPosition, width: subActualWidth } =
                    calculateBarPositionAndWidth(
                      subtask.actualStart,
                      subtask.actualEnd,
                      minDate.toISOString(),
                      maxDate.toISOString(),
                      dateRange.length * 100,
                      dateRange
                    );

                  return (
                    <React.Fragment key={subtask.id}>
                      <Tr
                        bg={getRowBackground(
                          subtask.status,
                          selectedTaskId === subtask.id
                        )}
                      >
                        <Td
                          colSpan={dateRange.length}
                          position="relative"
                          height="60px"
                          paddingLeft={`${level * 20}px`}
                        >
                          <TaskTooltip
                            task={subtask}
                            type="planned"
                            isSubtask={true}
                          >
                            <Box
                              position="absolute"
                              top="40%"
                              left={`${subPlannedPosition + 5}px`}
                              transform="translateY(-50%)"
                              width={`${subPlannedWidth}px`}
                              bg="purple.400"
                              height="14px"
                              cursor={"pointer"}
                              borderRadius="sm"
                            >
                              <Progress
                                position="absolute"
                                top="0"
                                w="100%"
                                h="14px"
                                value={subtask.progress}
                                bg="purple.400"
                                sx={{
                                  bg: "purple.400",
                                  "& > div": {
                                    backgroundColor: "purple.600",
                                    borderRadius: "sm",
                                  },
                                }}
                                borderRadius="sm"
                              />
                              <Text
                                position="absolute"
                                top="5%"
                                left="50%"
                                transform="translateX(-50%) translateY(-20%)"
                                color="white"
                                fontWeight="bold"
                                fontSize="xs"
                                maxW="100%"
                                whiteSpace="nowrap"
                                overflow="hidden"
                                pl={2}
                                textOverflow="ellipsis"
                              >
                                {subtask.name}
                              </Text>
                            </Box>
                          </TaskTooltip>
                          <DraggableOrStatic
                            isEditMode={isEditMode}
                            onStop={(e, data) => {
                              handleDragStop(subtask, data.x);
                            }}
                          >
                            <TaskTooltip
                              task={subtask}
                              type="actual"
                              isSubtask={true}
                            >
                              <Box
                                position="absolute"
                                top={isEditMode ? "15px" : "70%"}
                                left={
                                  isEditMode
                                    ? "0px"
                                    : `${subActualPosition + 5}px`
                                }
                                transform="translateY(-50%)"
                                width={`${subActualWidth}px`}
                                bg="yellow.400"
                                height="14px"
                                borderRadius="sm"
                                cursor={"pointer"}
                              >
                                {(() => {
                                  const aheadByDays = calculateDateDifference(
                                    subtask.plannedStart,
                                    subtask.actualStart
                                  );
                                  const overdueByDays = calculateDateDifference(
                                    subtask.actualEnd,
                                    subtask.plannedEnd
                                  );

                                  // Show "Ahead by X days" if the actual start is earlier than planned start
                                  if (
                                    aheadByDays > 0 &&
                                    new Date(subtask.actualStart) <
                                    new Date(subtask.plannedStart)
                                  ) {
                                    return (
                                      <Text
                                        position="absolute"
                                        left="-15px"
                                        top="50%"
                                        transform="translate(-100%, -50%)"
                                        fontSize="xs"
                                        color="blue.600"
                                        fontWeight="bold"
                                      >
                                        Ahead by {aheadByDays} day
                                        {aheadByDays > 1 ? "s" : ""}
                                      </Text>
                                    );
                                  }

                                  if (
                                    overdueByDays > 0 &&
                                    new Date(subtask.actualEnd) >
                                    new Date(subtask.plannedEnd)
                                  ) {
                                    return (
                                      <Text
                                        position="absolute"
                                        right="-15px"
                                        top="50%"
                                        transform="translate(100%, -50%)"
                                        fontSize="xs"
                                        color="red.600"
                                        fontWeight="bold"
                                      >
                                        Overdue by {overdueByDays} day
                                        {overdueByDays > 1 ? "s" : ""}
                                      </Text>
                                    );
                                  }

                                  return null; // No status if neither ahead nor overdue
                                })()}
                                {isEditMode && subtask.actualStart && (
                                  <svg
                                    width="20"
                                    height="20"
                                    style={{
                                      position: "absolute",
                                      left: "-7px",
                                      top: "50%",
                                      transform: "translate(-50%, -50%)",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <circle
                                      id={`${subtask.id}-start`}
                                      cx="10"
                                      cy="10"
                                      r="5"
                                      stroke="gray"
                                      fill="white"
                                      onMouseDown={(e) =>
                                        handleCircleDragStart(
                                          e,
                                          subtask.id,
                                          "start"
                                        )
                                      }
                                      onMouseUp={(e) =>
                                        handleCircleDragEnd(e, subtask.id)
                                      }
                                    />
                                  </svg>
                                )}
                                {isEditMode && subtask.actualStart && (
                                  <svg
                                    width="20"
                                    height="20"
                                    style={{
                                      position: "absolute",
                                      right: "-10px",
                                      top: "50%",
                                      transform: "translate(50%, -50%)",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <circle
                                      id={`${subtask.id}-end`}
                                      cx="7"
                                      cy="10"
                                      r="5"
                                      stroke="gray"
                                      fill="white"
                                      onMouseDown={(e) =>
                                        handleCircleDragStart(
                                          e,
                                          subtask.id,
                                          "end"
                                        )
                                      }
                                      onMouseUp={(e) =>
                                        handleCircleDragEnd(e, subtask.id)
                                      }
                                    />
                                  </svg>
                                )}
                                <Progress
                                  position="absolute"
                                  top="0"
                                  w="100%"
                                  h="14px"
                                  value={subtask.progress}
                                  bg="yellow.400"
                                  sx={{
                                    bg: "yellow.400",
                                    "& > div": {
                                      backgroundColor: "yellow.500",
                                      borderRadius: "sm",
                                    },
                                  }}
                                  borderRadius="sm"
                                />
                                <Text
                                  position="absolute"
                                  top="10%"
                                  left="50%"
                                  transform="translateX(-50%) translateY(-20%)"
                                  color="white"
                                  fontWeight="bold"
                                  fontSize="xs"
                                  maxW="100%"
                                  whiteSpace="nowrap"
                                  overflow="hidden"
                                  pl={2}
                                  textOverflow="ellipsis"
                                >
                                  {subtask.name}
                                </Text>
                              </Box>
                            </TaskTooltip>
                          </DraggableOrStatic>
                        </Td>
                      </Tr>
                      {/* Recursive call if subtask has subtasks and is expanded */}
                      {expandedTaskIds.has(subtask.id) &&
                        subtask.subtasks &&
                        renderSubtasks(subtask.subtasks, level + 1)}
                    </React.Fragment>
                  );
                });
              };

              return (
                <React.Fragment key={task.id}>
                  <Tr bg={selectedTaskId === task.id ? "blue.100" : "white"}>
                    <Td
                      colSpan={dateRange.length}
                      position="relative"
                      height="60px"
                      borderColor={"gray.200"}
                      borderRightWidth="1px"
                    >
                      <TaskTooltip task={task} type="planned">
                        <Box
                          position="absolute"
                          top="40%"
                          cursor={"pointer"}
                          left={`${plannedPosition + 15}px`}
                          transform="translateY(-50%)"
                          width={`${plannedWidth}px`}
                          height="14px"
                          borderRadius="sm"
                        >
                          <Progress
                            position="absolute"
                            top="0"
                            w="100%"
                            h="14px"
                            value={task.progress}
                            bg="blue.400"
                            borderRadius="sm"
                          />
                          <Text
                            position="absolute"
                            top="5%"
                            left="50%"
                            transform="translateX(-50%) translateY(-20%)"
                            color="white"
                            fontWeight="bold"
                            fontSize="xs"
                            maxW="100%"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            pl={2}
                            textOverflow="ellipsis"
                          >
                            {task.name}
                          </Text>
                        </Box>
                      </TaskTooltip>
                      <DraggableOrStatic
                        isEditMode={isEditMode}
                        onStop={(e, data) => handleDragStop(task, data.x)}
                      >
                        <TaskTooltip task={task} type="actual">
                          <Box
                            position="absolute"
                            top={isEditMode ? "10px" : "68%"}
                            cursor="pointer"
                            left={`${actualPosition + 15}px`}
                            transform="translateY(-50%)"
                            width={`${actualWidth}px`}
                            bg="green.400"
                            height="14px"
                            borderRadius="sm"
                          >
                            {(() => {
                              const aheadByDays = calculateDateDifference(
                                task.plannedStart,
                                task.actualStart
                              );
                              const overdueByDays = calculateDateDifference(
                                task.actualEnd,
                                task.plannedEnd
                              );

                              // Show "Ahead by X days" if the actual start is earlier than planned start
                              if (
                                aheadByDays > 0 &&
                                new Date(task.actualStart) <
                                new Date(task.plannedStart)
                              ) {
                                return (
                                  <Text
                                    position="absolute"
                                    left="-15px"
                                    top="50%"
                                    transform="translate(-100%, -50%)"
                                    fontSize="xs"
                                    color="blue.600"
                                    fontWeight="bold"
                                  >
                                    Ahead by {aheadByDays} day
                                    {aheadByDays > 1 ? "s" : ""}
                                  </Text>
                                );
                              }

                              if (
                                overdueByDays > 0 &&
                                new Date(task.actualEnd) >
                                new Date(task.plannedEnd)
                              ) {
                                return (
                                  <Text
                                    position="absolute"
                                    right="-15px"
                                    top="50%"
                                    transform="translate(100%, -50%)"
                                    fontSize="xs"
                                    color="red.600"
                                    fontWeight="bold"
                                    whiteSpace="nowrap"
                                    overflow="hidden"
                                    pl={2}
                                    textOverflow="ellipsis"
                                  >
                                    Overdue by {overdueByDays} day
                                    {overdueByDays > 1 ? "s" : ""}
                                  </Text>
                                );
                              }

                              return null; // No status if neither ahead nor overdue
                            })()}

                            {isEditMode && task.actualStart && (
                              <svg
                                width="20"
                                height="20"
                                style={{
                                  position: "absolute",
                                  left: "-7px",
                                  top: "50%",
                                  transform: "translate(-50%, -50%)",
                                  cursor: "pointer",
                                }}
                              >
                                <circle
                                  id={`${task.id}-start`}
                                  cx="10"
                                  cy="10"
                                  r="5"
                                  stroke="gray"
                                  fill="white"
                                  onMouseDown={(e) =>
                                    handleCircleDragStart(e, task.id, "start")
                                  }
                                  onMouseUp={(e) =>
                                    handleCircleDragEnd(e, task.id)
                                  }
                                />
                              </svg>
                            )}
                            {isEditMode && task.actualStart && (
                              <svg
                                width="20"
                                height="20"
                                style={{
                                  position: "absolute",
                                  right: "-10px",
                                  top: "50%",
                                  transform: "translate(50%, -50%)",
                                  cursor: "pointer",
                                }}
                              >
                                <circle
                                  id={`${task.id}-end`}
                                  cx="7"
                                  cy="10"
                                  r="5"
                                  stroke="gray"
                                  fill="white"
                                  onMouseDown={(e) =>
                                    handleCircleDragStart(e, task.id, "end")
                                  }
                                  onMouseUp={(e) =>
                                    handleCircleDragEnd(e, task.id)
                                  }
                                />
                              </svg>
                            )}
                            <Progress
                              position="absolute"
                              top="0"
                              w="100%"
                              h="14px"
                              value={task.progress}
                              bg="green.400"
                              sx={{
                                bg: "green.400",
                                "& > div": {
                                  backgroundColor: "green.600",
                                  borderRadius: "sm",
                                },
                              }}
                              borderRadius="sm"
                            />
                            <Text
                              position="absolute"
                              top="10%"
                              left="50%"
                              transform="translateX(-50%) translateY(-20%)"
                              color="white"
                              fontWeight="bold"
                              fontSize="xs"
                              maxW="100%"
                              whiteSpace="nowrap"
                              overflow="hidden"
                              pl={2}
                              textOverflow="ellipsis"
                            >
                              {task.name}
                            </Text>
                          </Box>
                        </TaskTooltip>
                      </DraggableOrStatic>
                    </Td>
                  </Tr>

                  {expandedTaskIds.has(task.id) &&
                    task.subtasks &&
                    renderSubtasks(task.subtasks)}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  };
const Gantt: React.FC<GanttProps> = ({
  selectedTask,
  organizationId,
  projectId,
  channelId,
  bg,
  users,
  color,
  onExit,
  filteredProjects,
  currentUser,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const taskListRef = useRef<any>(null);
  const [panelWidth, setPanelWidth] = useState("35%");
  const [isDragging, setIsDragging] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string>("");
  const [zoomLevel, setZoomLevel] = useState<string>("days");
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [task, setTask] = useState<Task[]>([]);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isTimelineVisible, setIsTimelineVisible] = useState<boolean>(false);

  const [expandedTaskIds, setExpandedTaskIds] = useState<Set<string>>(
    new Set()
  );
  const toggleExpandedTask = (taskId: string) => {
    setExpandedTaskIds((prevExpandedTaskIds) => {
      const newExpandedTaskIds = new Set(prevExpandedTaskIds);
      if (newExpandedTaskIds.has(taskId)) {
        newExpandedTaskIds.delete(taskId); // Collapse the task
      } else {
        newExpandedTaskIds.add(taskId); // Expand the task
      }
      return newExpandedTaskIds;
    });
  };

  const taskPerms = React.useMemo(
    () => getTaskPermissions(currentUser),
    [currentUser]
  );
  const { isAdmin, canCreate, canEdit, canDelete } = taskPerms;

  // Same combination as in TaskListPanel: a per-project admin/editor grant adds
  // task add/edit rights on top of the org-wide permission (never delete).
  const projectScope = React.useMemo(
    () => getProjectScopeAccess(currentUser, projectId),
    [currentUser, projectId]
  );
  const canCreateTask = canCreate || projectScope.canManageTasks;
  const canEditTask = React.useCallback(
    (task: any) => canEdit(task) || projectScope.canManageTasks,
    [canEdit, projectScope]
  );

  const showPermissionToast = () => {
    showToast({
      title: "Permission Restricted",
      description: "You do not have permission to perform this action.",
      status: "warning",
      duration: 3000,
      isClosable: true,
    });
  };

  // const fetchTasks = async () => {
  //   try {
  //     const tasksCollection = collection(db, "tasks");
  //     const taskDocs = await getDocs(tasksCollection);
  //     const fetchedTasks: Task[] = taskDocs.docs.map((doc) => ({
  //       id: doc.id,
  //       ...doc.data(),
  //     })) as Task[];
  //     setTask(fetchedTasks);
  //   } catch (error) {
  //     console.error("Error fetching tasks: ", error);
  //     alert("Failed to fetch tasks. Please try again.");
  //   }
  // };

  // useEffect(() => {
  //   fetchTasks();
  // }, [task]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (isDragging) {
      const deltaX = event.clientX - startX; // Calculate how much the mouse has moved
      const newWidth =
        Math.min(Math.max(startWidth + deltaX, 150), window.innerWidth - 200) +
        "px";
      setPanelWidth(newWidth);
    }
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(event.clientX);
    setStartWidth(parseInt(panelWidth, 10)); // Store the current width
  };

  const handleSync = () => {
    window.location.reload();
  };

  const toggleEditMode = () => {
    setIsEditMode((prev) => !prev);
  };

  const toggleTimelineVisibility = () => {
    setIsTimelineVisible((prev) => !prev);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const ganttContainerRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isViewingResource, setIsViewingResource] = useState(false);

  useEffect(() => {
    const onFsChange = () => {
      const fsActive = Boolean(document.fullscreenElement);
      setIsFullscreen(fsActive);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // useEffect(() => {
  //   setIsTimelineVisible(isFullscreen);
  // }, [isFullscreen]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await ganttContainerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {
      console.error("Fullscreen toggle failed", e);
      showToast({
        title: "Action failed",
        description: "Could not toggle fullscreen",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  return (
    <TaskContext.Provider value={{ expandedTaskIds, toggleExpandedTask }}>
      <Box
        ref={ganttContainerRef}
        bg={isDark ? "#1A1C1F" : "white"}
        color={isDark ? "white" : "#111827"}
        width="100%"
        minH={isFullscreen ? "100vh" : undefined}
      >
        {!isFullscreen && (
          <Flex mb={2} gap={4} align="center">
            <Tooltip label={selectedTask?.title || "Task"} hasArrow>
              <Text
                fontSize="lg"
                fontWeight="bold"
                maxWidth="150px"
                isTruncated
                cursor="pointer"
              >
                {selectedTask?.title || "Task"}
              </Text>
            </Tooltip>

            {/* Add Task Button */}
            {canCreateTask && (
              <Tooltip label="Add Task" hasArrow>
                <Button
                  onClick={() => {
                    taskListRef.current?.handleAddTaskClick();
                  }}
                  variant="gold"
                  size="sm"
                  display={"flex"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  borderRadius="full"
                  p={2}
                  _hover={{ transform: "scale(1.05)" }}
                  opacity={1}
                >
                  <AddIcon />
                </Button>
              </Tooltip>
            )}

            {/* Deleted Audits Button */}
            {isAdmin && (
              <Tooltip label="Deleted Audits" hasArrow>
                <Button
                  onClick={() => {
                    taskListRef.current?.fetchDeletedAudits();
                  }}
                  variant="gold"
                  size="sm"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p={2}
                  _hover={{ transform: "scale(1.05)" }}
                >
                  <DeleteIcon />
                </Button>
              </Tooltip>
            )}

            {/* Export Button */}
            <Tooltip label="Export Data" hasArrow>
              <Button
                onClick={() => taskListRef.current?.handleExportClick()}
                variant="gold"
                size="sm"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={2}
                _hover={{ transform: "scale(1.05)" }}
              >
                <DownloadIcon />
              </Button>
            </Tooltip>

            {/* Import Button with Options */}
            <ChakraMenu>
              <Tooltip label="Import Options" hasArrow>
                <ChakraMenuButton
                  as={Button}
                  variant="gold"
                  size="sm"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p={2}
                  _hover={{ transform: "scale(1.05)" }}
                >
                  <IconWrapper icon={FiUpload} size={18} />
                </ChakraMenuButton>
              </Tooltip>
              <Portal>
                <ChakraMenuList zIndex={3000} bg={isDark ? "#1A1C1F" : "white"} borderColor={isDark ? "#2E2F33" : "gray.200"}>
                  <ChakraMenuItem 
                    onClick={() => taskListRef.current?.handleDownloadTemplate()}
                    icon={<DownloadIcon />}
                    bg={isDark ? "#1A1C1F" : "white"}
                    _hover={{ bg: isDark ? "#2A3441" : "gray.50" }}
                    fontSize="sm"
                  >
                    Download Sample Template
                  </ChakraMenuItem>
                  {isAdmin && (
                    <ChakraMenuItem
                      onClick={() => taskListRef.current?.handleImportClick()}
                      icon={<IconWrapper icon={FiUpload} size={14} />}
                      bg={isDark ? "#1A1C1F" : "white"}
                      _hover={{ bg: isDark ? "#2A3441" : "gray.50" }}
                      fontSize="sm"
                    >
                      Upload Task File
                    </ChakraMenuItem>
                  )}
                </ChakraMenuList>
              </Portal>
            </ChakraMenu>

            {/* Print Button */}
            <Tooltip label="Print Report" hasArrow>
              <Button
                onClick={() => taskListRef.current?.handlePrintClick()}
                variant="gold"
                size="sm"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={2}
                _hover={{ transform: "scale(1.05)" }}
              >
                <IconWrapper icon={AiFillPrinter} size={20} />
              </Button>
            </Tooltip>

            {/* Fullscreen Toggle */}
            <Tooltip label={"Fullscreen"} hasArrow>
              <Button
                onClick={toggleFullscreen}
                variant="gold"
                size="sm"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={2}
                _hover={{ transform: "scale(1.05)" }}
              >
                <IconWrapper icon={FiMaximize} size={18} />
              </Button>
            </Tooltip>

            {/* Resource View Toggle */}
            <Tooltip label="Resource Timeline" hasArrow>
              <Button
                onClick={() => setIsViewingResource(!isViewingResource)}
                variant="gold"
                size="sm"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={2}
                _hover={{ transform: "scale(1.05)" }}
              >
                <IconWrapper icon={BsPeopleFill} size={18} />
              </Button>
            </Tooltip>

            <Tooltip label="Back to tasks" hasArrow>
              <IconButton
                ml="auto"
                aria-label="Back to tasks"
                icon={<IconWrapper icon={FiArrowLeft} size={18} />} // ⇦ custom icon here
                size="sm"
                variant="gold"
                borderRadius="full"
                onClick={onExit} // your callback, e.g. navigate(-1)
                _hover={{ transform: "scale(1.05)" }}
                _focus={{ boxShadow: "none" }}
              />
            </Tooltip>

            {/* <Tooltip
            label={isTimelineVisible ? "Hide Timeline" : "Show Timeline"}
            hasArrow
          >
            <Button
              onClick={toggleTimelineVisibility}
              bg={isTimelineVisible ? "gray.500" : "purple.500"}
              color="white"
              size="sm"
              borderRadius="full"
              _hover={{ transform: "scale(1.1)" }}
              _active={{ transform: "scale(1.05)" }}
            >
              {isTimelineVisible ? "Hide Timeline" : "Show Timeline"}
            </Button>
          </Tooltip> }

          {/* Zoom Selector */}

            {isTimelineVisible && (
              <Flex ml={5} align="center">
                <Text fontSize={"sm"} mr={2}>
                  Zoom:
                </Text>
                <Select
                  width="120px"
                  height={"30px"}
                  fontSize={"sm"}
                  value={zoomLevel}
                  onChange={(e) => setZoomLevel(e.target.value)}
                >
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                  <option value="quarters">Quarters</option>
                  <option value="years">Years</option>
                </Select>
              </Flex>
            )}

            {/* Edit Mode Toggle */}
            {isTimelineVisible && (canEditTask(undefined) || canCreateTask) && (
              <Tooltip
                label={isEditMode ? "Disable Edit Mode" : "Enable Edit Mode"}
                hasArrow
              >
                <Button
                  size="sm"
                  ml={4}
                  minWidth="100px"
                  onClick={toggleEditMode}
                  colorScheme={isEditMode ? "red" : "blue"}
                  leftIcon={<EditIcon />}
                  borderRadius="md"
                  _hover={{ transform: "scale(1.05)" }}
                >
                  {isEditMode ? "Disable Edit" : "Enable Edit"}
                </Button>
              </Tooltip>
            )}
          </Flex>
        )}

        <Flex style={{ height: isFullscreen ? "100vh" : "85vh" }} w="100%">
          {isViewingResource ? (
            <Box w="100%" h="100%">
              <ResourceView
                tasks={[{ id: selectedTask.id, title: selectedTask.title, data: task }]}
                users={users}
                onBack={() => setIsViewingResource(false)}
                isDark={isDark}
              />
            </Box>
          ) : (
            <>
              <TaskListPanel
                ref={taskListRef}
                users={users}
                currentUser={currentUser}
                filteredProjects={filteredProjects}
                bg={bg}
                color={color}
                tasks={task}
                setTask={setTask}
                width={isTimelineVisible ? panelWidth : "100%"}
                organizationId={organizationId}
                selectedTaskId={selectedTaskId}
                onSelectTask={setSelectedTaskId}
                zoomLevel={zoomLevel}
                TaskName={selectedTask.title || ""}
                taskId={selectedTask.id || ""}
                projectId={projectId}
                channelId={channelId}
                expandedTaskId={expandedTaskId}
                setExpandedTaskId={setExpandedTaskId}
                isFullscreen={isFullscreen}
                fullscreenContainerRef={ganttContainerRef}
              />
              {isTimelineVisible && (
                <Box
                  width="4px"
                  bg={isDark ? "#2E2F33" : "gray.300"}
                  cursor="col-resize"
                  onMouseDown={handleMouseDown}
                />
              )}

              {isTimelineVisible && (
                <TimelinePanel
                  width={`calc(100% - ${panelWidth})`}
                  taskId={selectedTask.id || ""}
                  projectId={projectId}
                  handleSync={handleSync}
                  channelId={channelId}
                  organizationId={organizationId}
                  selectedTaskId={selectedTaskId}
                  zoomLevel={zoomLevel}
                  isEditMode={isEditMode}
                  setIsEditMode={setIsEditMode}
                  onZoomChange={setZoomLevel}
                  expandedTaskId={expandedTaskId}
                  tasks={task}
                  setTask={setTask}
                />
              )}
            </>
          )}
        </Flex>
      </Box>
    </TaskContext.Provider>
  );
};

export const useTaskContext = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error("useTaskContext must be used within a TaskProvider");
  }
  return context;
};

export default Gantt;
